import React, { useState, useEffect } from "react";
import { formatTier, tierKey } from "../lib/tierLabel";
import AssetPicker from "./AssetPicker";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  Users,
  Database,
  Building2,
  Truck,
  Link2,
  Target,
  RefreshCw,
  Trash2,
  Info,
  TrendingUp,
  Zap,
  FileText,
  HelpCircle,
  Network,
  Cable,
  Shield,
  DollarSign,
  MessageSquare,
  Settings,
  MoreHorizontal,
  Monitor,
  Home,
  Sparkles,
  Loader2,
} from "lucide-react";
import {
  ImpactCategoryHoverCard,
  ImpactInfoButton,
} from "../components/ImpactDefinitionTooltip";

// ─── Constants ───────────────────────────────────────────────────
const FALLBACK_DEPARTMENTS = [
  "IT / Technology", "Finance", "Operations", "Human Resources",
  "Sales & Marketing", "Customer Service", "Legal & Compliance",
  "Supply Chain", "Research & Development", "Executive", "Other",
];

const IMPACT_LEVELS = [
  { value: "no_impact", label: "No Impact", color: "bg-slate-100 text-slate-600" },
  { value: "low", label: "Low", color: "bg-emerald-100 text-emerald-700" },
  { value: "medium", label: "Medium", color: "bg-amber-100 text-amber-700" },
  { value: "high", label: "High", color: "bg-red-100 text-red-700" },
];

const TIME_BANDS = [
  { id: "band_1", name: "0-4h", label: "Critical", hours: 4 },
  { id: "band_2", name: "4-24h", label: "Urgent", hours: 24 },
  { id: "band_3", name: "24-72h", label: "Operational", hours: 72 },
  { id: "band_4", name: "72+h", label: "Strategic", hours: 168 },
];

const IMPACT_CATEGORIES = [
  { key: "financial_impact", label: "Financial", description: "Revenue loss, costs" },
  { key: "legal_impact", label: "Legal", description: "Contracts, liability" },
  { key: "regulatory_impact", label: "Regulatory", description: "Compliance, penalties" },
  { key: "reputational_impact", label: "Reputational", description: "Brand, trust" },
  { key: "operational_impact", label: "Operational", description: "Processes, delivery" },
];

const DEPENDENCY_TYPES = [
  { id: "data_information", label: "Data / Information", icon: Database, color: "#06b6d4" },
  { id: "technology_system", label: "Technology / System", icon: Monitor, color: "#8b5cf6" },
  { id: "personnel_expertise", label: "Personnel / Expertise", icon: Users, color: "#ec4899" },
  { id: "financial_budgetary", label: "Financial / Budgetary", icon: DollarSign, color: "#10b981" },
  { id: "regulatory_compliance", label: "Regulatory / Compliance", icon: Shield, color: "#f59e0b" },
  { id: "infrastructure_facility", label: "Infrastructure / Facility", icon: Building2, color: "#78716c" },
  { id: "supply_chain_vendor", label: "Supply Chain / Vendor", icon: Truck, color: "#14b8a6" },
  { id: "communication_approval", label: "Communication / Approval", icon: MessageSquare, color: "#6366f1" },
  { id: "operational_output", label: "Operational Output", icon: Settings, color: "#0ea5e9" },
  { id: "other", label: "Other", icon: MoreHorizontal, color: "#94a3b8" },
];

const DEPENDENCY_CRITICALITY = [
  { id: "critical", label: "Critical", color: "#ef4444", desc: "Process halts completely" },
  { id: "high", label: "High", color: "#f59e0b", desc: "Major degradation" },
  { id: "medium", label: "Medium", color: "#3b82f6", desc: "Reduced capacity" },
  { id: "low", label: "Low", color: "#10b981", desc: "Minor inconvenience" },
];

const WIZARD_STEPS = [
  { id: 1, title: "Process Info", icon: FileText },
  { id: 2, title: "Impact Matrix", icon: Target },
  { id: 3, title: "Dependencies", icon: Link2 },
  { id: 4, title: "Resources", icon: Users },
  { id: 5, title: "Review", icon: CheckCircle2 },
];

const DEFAULT_IMPACT_ROW = { band_1: "no_impact", band_2: "no_impact", band_3: "no_impact", band_4: "no_impact" };

const DEFAULT_FORM_DATA = {
  process_name: "",
  department: "",
  description: "",
  process_owner_name: "",
  process_owner_email: "",
  process_owner_phone: "",
  process_owner_stakeholder_id: null,
  asset_types: [],
  // V22.9 — Technical BIA fields (default to business mode)
  process_category: "business",
  is_it_process: false,
  it_function_scope: "",
  financial_impact: { ...DEFAULT_IMPACT_ROW },
  legal_impact: { ...DEFAULT_IMPACT_ROW },
  regulatory_impact: { ...DEFAULT_IMPACT_ROW },
  reputational_impact: { ...DEFAULT_IMPACT_ROW },
  operational_impact: { ...DEFAULT_IMPACT_ROW },
  mbco_percentage: 50,
  mbco_description: "",
  upstream_process_ids: [],
  downstream_process_ids: [],
  upstream_dependency_details: {},
  downstream_dependency_details: {},
  custom_upstream_dependencies: [],
  custom_downstream_dependencies: [],
  application_dependencies: [],
  custom_application_dependencies: [],
  total_staff: 0,
  minimum_staff: 0,
  time_to_min_staff_hours: 0,
  staffing_roles: [],
  asset_data_sensitivity: [],
  remaining_resource_recovery_hours: 0,
  department_head_id: "",
  department_head_name: "",
  department_head_email: "",
  data_loss_tolerance_hours: 24,
  data_loss_financial_impact: 0,
  critical_vendors: [],
  vendor_dependencies: [],
  critical_facilities: [],
  facility_dependencies: [],
  remote_operation_possible: false,
  resource_is_remote_capable: false,
  primary_facility_id: "",
};

const ASSET_TYPE_OPTIONS = [
  { id: "data_center", name: "Data Center", icon: "\u{1F3E2}", description: "Primary and secondary data center facilities" },
  { id: "vendor_supply_chain", name: "Vendor / Supply Chain", icon: "\u{1F4E6}", description: "Third-party vendors and partners" },
  { id: "it_infrastructure", name: "IT Infrastructure", icon: "\u{1F5A5}\u{FE0F}", description: "Servers, networks, hardware, cloud" },
  { id: "information_assets", name: "Information Assets", icon: "\u{1F4CA}", description: "Data, databases, intellectual property" },
  { id: "work_area_facility", name: "Work-Area / Facility", icon: "\u{1F3ED}", description: "Office locations, manufacturing sites" },
  { id: "people_staff", name: "People / Staff", icon: "\u{1F465}", description: "Employees, contractors, key personnel" },
  { id: "business_process", name: "Business Process", icon: "\u{2699}\u{FE0F}", description: "Critical workflows, procedures, operations" },
  { id: "business_application", name: "Business Application", icon: "\u{1F4BB}", description: "Critical software applications and systems" },
];

const IMPACT_RANK = { no_impact: 0, low: 1, medium: 2, high: 3 };
const BAND_ORDER = ["band_1", "band_2", "band_3", "band_4"];

// ─── V22.9.8 — Federated Upstream/Downstream Process surface ─────────────────
// Reads `details.upstream_asset_ids` and `details.downstream_asset_ids` from
// the asset_profiles of the user's selected Application Dependencies and
// surfaces them as one-click suggestions for the BIA's upstream / downstream
// dependency lists. Same hybrid pattern as the Connectivity surface:
// "Quick Add" commits immediately as a custom dependency string; "Edit"
// pre-fills the manual input.
const FederatedProcessDepsSurfaceImpl = ({
  api,
  applicationIds,
  direction,          // 'upstream' | 'downstream'
  alreadySelected,    // string[] — already in custom_{direction}_dependencies
  onQuickAdd,         // (name) => void
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!applicationIds || applicationIds.length === 0) {
        setItems([]); return;
      }
      setLoading(true);
      try {
        const profiles = await Promise.all(
          applicationIds.map((id) =>
            api.get(`/asset-profiles/by-id/${id}`).then((r) => r.data).catch(() => null),
          ),
        );
        if (cancelled) return;
        const key = direction === "upstream" ? "upstream_asset_ids" : "downstream_asset_ids";
        const merged = new Map();  // dedupe by name
        for (const p of profiles) {
          const list = (p?.details?.[key] || []);
          for (const name of list) {
            const trimmed = (name || "").toString().trim();
            if (!trimmed) continue;
            if (!merged.has(trimmed.toLowerCase())) {
              merged.set(trimmed.toLowerCase(), { name: trimmed, source_app: p?.asset_name });
            }
          }
        }
        setItems(Array.from(merged.values()));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [api, JSON.stringify(applicationIds), direction]);

  if (!applicationIds || applicationIds.length === 0) return null;
  const isAlreadyAdded = (n) =>
    (alreadySelected || []).some((x) => (x || "").toLowerCase() === (n || "").toLowerCase());

  const accent = direction === "upstream"
    ? { ring: "border-sky-200 bg-sky-50/60", text: "text-sky-900", chip: "bg-white border-sky-200 text-sky-700", btn: "bg-sky-600 hover:bg-sky-700" }
    : { ring: "border-emerald-200 bg-emerald-50/60", text: "text-emerald-900", chip: "bg-white border-emerald-200 text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700" };

  return (
    <div className={`mt-3 p-3 border rounded-lg ${accent.ring}`} data-testid={`bia-federated-${direction}-surface`}>
      <div className="flex items-center justify-between mb-2">
        <Label className={`text-xs font-semibold flex items-center gap-1.5 ${accent.text}`}>
          <Sparkles className="w-3.5 h-3.5" />
          Federated {direction === "upstream" ? "Upstream" : "Downstream"} from selected applications ({items.length})
          <Badge variant="outline" className={`text-[9px] ${accent.chip}`}>From Application Profile</Badge>
        </Label>
        {loading && <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />}
      </div>
      {items.length === 0 ? (
        <p className={`text-[11px] italic ${accent.text} opacity-70`}>
          No federated {direction} dependencies on the selected applications. Add them in the App Profile's Dependencies step to surface here.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {items.map((item, idx) => {
            const added = isAlreadyAdded(item.name);
            return (
              <div key={idx} className="inline-flex items-center gap-1 bg-white border border-slate-200 rounded px-1.5 py-0.5 text-[11px]" data-testid={`bia-federated-${direction}-${idx}`}>
                <span className="font-medium text-slate-700">{item.name}</span>
                {item.source_app && <span className="text-[9px] text-slate-400">· from {item.source_app}</span>}
                {added ? (
                  <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 ml-1">Added</Badge>
                ) : (
                  <button
                    type="button"
                    onClick={() => onQuickAdd?.(item.name)}
                    className={`text-[10px] text-white px-1.5 py-0.5 rounded ml-1 ${accent.btn}`}
                    data-testid={`bia-federated-${direction}-${idx}-quick-add`}
                  >
                    + Quick Add
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── V22.9.8 — Connectivity Links surface (BIA Wizard wiring) ────────────────
// Fetches vendor_connectivity_links federated from the selected Application
// Profiles' Step 5. Surfaces them as one-click vendor-dependency suggestions
// so the BIA accurately captures VPN/API/SFTP integrations the app talks to.
// Profiles' Step 5. Surfaces them as one-click vendor-dependency suggestions
// so the BIA accurately captures VPN/API/SFTP integrations the app talks to.
const ConnectivityLinksSurfaceImpl = ({ api, applicationIds, onAddVendorDep, onQuickAddVendorDep, vendorDependencies }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!applicationIds || applicationIds.length === 0) {
        setLinks([]); return;
      }
      setLoading(true);
      try {
        // Fetch links for each linked app; merge results
        const responses = await Promise.all(
          applicationIds.map((id) =>
            api.get("/asset-profiles/connectivity-links", { params: { application_id: id } }).catch(() => ({ data: { links: [] } })),
          ),
        );
        if (cancelled) return;
        const merged = responses.flatMap((r) => r.data?.links || []);
        setLinks(merged);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [api, JSON.stringify(applicationIds)]);

  if (!applicationIds || applicationIds.length === 0) return null;
  const isAlreadyAdded = (vendorName) =>
    (vendorDependencies || []).some(
      (v) => (v.vendor_name || "").toLowerCase() === (vendorName || "").toLowerCase(),
    );
  return (
    <div className="mt-4 p-3 bg-violet-50/60 border border-violet-200 rounded-lg" data-testid="bia-connectivity-links-surface">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-semibold text-violet-900 flex items-center gap-1.5">
          <Cable className="w-3.5 h-3.5" />
          Federated Vendor Connectivity ({links.length})
          <Badge variant="outline" className="text-[9px] bg-white border-violet-200 text-violet-700">From Application Profile Step 5</Badge>
        </Label>
        {loading && <RefreshCw className="w-3 h-3 animate-spin text-violet-400" />}
      </div>
      {links.length === 0 ? (
        <p className="text-[11px] text-violet-700/70 italic">No federated connectivity rows on the selected applications. Add them in the App Profile's Step 5 to surface here.</p>
      ) : (
        <div className="space-y-1">
          {links.slice(0, 8).map((l, idx) => {
            const added = isAlreadyAdded(l.vendor_name);
            return (
              <div key={idx} className="flex items-center justify-between gap-2 bg-white border border-violet-100 rounded px-2 py-1.5 text-[11.5px]" data-testid={`bia-connectivity-row-${idx}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 flex-shrink-0">{l.protocol || "API"}</Badge>
                  <span className="font-medium text-slate-700 truncate">{l.vendor_name || "—"}</span>
                  <span className="text-slate-400 text-[10.5px] truncate">
                    {l.destination_ip}{l.port ? ` :${l.port}` : ""}
                  </span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {added ? (
                    <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" data-testid={`bia-connectivity-row-${idx}-already-added`}>
                      Added
                    </Badge>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => onQuickAddVendorDep?.(l)}
                        className="text-[10.5px] px-2 py-0.5 rounded bg-violet-600 hover:bg-violet-700 text-white font-medium"
                        data-testid={`bia-connectivity-row-${idx}-quick-add`}
                        title="Add immediately with inherited criticality — you can fill the contact email later"
                      >
                        Quick Add
                      </button>
                      <button
                        type="button"
                        onClick={() => onAddVendorDep?.(l.vendor_name)}
                        className="text-[10.5px] text-violet-700 hover:text-violet-900 underline-offset-2 hover:underline"
                        data-testid={`bia-connectivity-row-${idx}-edit-details`}
                      >
                        Edit Details →
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {links.length > 8 && (
            <p className="text-[10px] text-violet-600 text-center mt-1">+{links.length - 8} more rows</p>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────
export default function BIAWizardDialog({
  open,
  onOpenChange,
  api,
  availableProcesses,
  applicationProfiles,
  applicationProfilesLoading,
  stakeholders,
  onSuccess,
  triggerUpgradeWall,
  processCount,
  getRTOBandColor,
  // V22.9 — "business" (default) or "technical" (IT Function Manager backbone-infra BIA)
  mode = "business",
  // When mode === "technical", lock the business_unit to this IT-line department
  lockedDepartment = "",
  // Optional: pre-fill name/department/description (e.g. completing an onboarding draft)
  prefill = null,
  // Task 0 — full existing record to edit; when set, wizard saves via PUT (reopen-on-edit fires)
  editProcess = null,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });
  const [tempVendor, setTempVendor] = useState("");
  const [tempFacility, setTempFacility] = useState("");
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [showFacilityForm, setShowFacilityForm] = useState(false);
  const [newVendor, setNewVendor] = useState({
    vendor_name: "", vendor_type: "technology", criticality: "medium",
    contact_name: "", contact_email: "", contact_phone: "", address: "", services_provided: "",
  });
  const [newFacility, setNewFacility] = useState({
    facility_name: "", facility_type: "primary", address: "", city: "",
    activation_time_hours: 0, capacity_percentage: 100, is_remote_capable: false,
  });
  const [customUpstream, setCustomUpstream] = useState("");
  const [customDownstream, setCustomDownstream] = useState("");
  const [customAppDependency, setCustomAppDependency] = useState("");
  const [calculatedPreview, setCalculatedPreview] = useState(null);
  const [stakeholderSearch, setStakeholderSearch] = useState("");
  const [showStakeholderDropdown, setShowStakeholderDropdown] = useState(false);
  const [orgDepartments, setOrgDepartments] = useState([]);

  // AI RTO/RPO suggestion state
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null); // {tier, rto_hours, rpo_hours, reasoning, confidence, assumptions}

  const runAiSuggest = async () => {
    if (!formData.process_name) {
      toast.error("Enter the process name first");
      return;
    }
    setAiSuggesting(true);
    try {
      const res = await api.post("/bia-ai/suggest-rto-rpo", {
        process_name: formData.process_name,
        process_description: formData.process_description || "",
        department: formData.department || "",
        dependencies: [
          ...(formData.upstream_dependencies || []),
          ...(formData.downstream_dependencies || []),
          ...(formData.application_dependencies || []),
        ].map(d => typeof d === "string" ? d : (d?.name || d?.process_name || "")),
        regulatory_drivers: formData.regulatory_drivers || [],
        peak_users: formData.peak_users || 0,
        revenue_impact_usd_per_hour: formData.revenue_impact_per_hour || 0,
      });
      if (res.data?.success) {
        setAiSuggestion(res.data.suggestion);
        const src = res.data.source === "gpt-4o" ? "AI" : "Fallback";
        toast.success(`AI suggests Tier ${res.data.suggestion.tier} · RTO ${res.data.suggestion.rto_hours}h · RPO ${res.data.suggestion.rpo_hours}h (${src})`);
      }
    } catch (e) {
      toast.error("AI suggestion failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setAiSuggesting(false);
    }
  };

  // Fetch org departments for dropdown
  useEffect(() => {
    if (open && api) {
      api.get("/bcm-admin/departments").then(res => {
        const depts = (res.data || []).map(d => d.name).filter(Boolean);
        let list = depts.length > 0 ? [...depts, "Other"] : [...FALLBACK_DEPARTMENTS];
        // Keep any prefilled (onboarding-seeded) department selectable.
        if (prefill?.department && !list.includes(prefill.department)) {
          list = [prefill.department, ...list];
        }
        setOrgDepartments(list);
      }).catch(() => setOrgDepartments(FALLBACK_DEPARTMENTS));
    }
  }, [open, api, prefill]);

  // Calculate RTO/RPO preview when impact matrix changes
  useEffect(() => {
    calculatePreview();
  }, [formData.financial_impact, formData.legal_impact, formData.regulatory_impact,
      formData.reputational_impact, formData.operational_impact,
      formData.data_loss_tolerance_hours, formData.data_loss_financial_impact,
      formData.staffing_roles, formData.asset_data_sensitivity,
      formData.total_staff, formData.minimum_staff, formData.time_to_min_staff_hours]);

  const calculatePreview = () => {
    const matrix = {
      financial_impact: formData.financial_impact,
      legal_impact: formData.legal_impact,
      regulatory_impact: formData.regulatory_impact,
      reputational_impact: formData.reputational_impact,
      operational_impact: formData.operational_impact,
    };
    const bands = ["band_1", "band_2", "band_3", "band_4"];
    const bandHours = { band_1: 4, band_2: 24, band_3: 72, band_4: 168 };
    const bandToTier = { band_1: "tier_1", band_2: "tier_2", band_3: "tier_3", band_4: "tier_4" };
    const categories = ["financial_impact", "legal_impact", "regulatory_impact", "reputational_impact", "operational_impact"];

    const calculateRPO = () => {
      const impact = formData.data_loss_financial_impact || 0;
      const tolerance = formData.data_loss_tolerance_hours || 24;
      if (impact > 500000) return Math.min(tolerance, 1);
      if (impact > 100000) return Math.min(tolerance, 4);
      if (impact > 50000) return Math.min(tolerance, 8);
      return Math.min(tolerance, 24);
    };
    // D2 — strictest per-asset RPO overrides the process-level fallback
    const rpo_hours = strictestAssetRpo ? strictestAssetRpo.rpo : calculateRPO();
    // D1 — staff-recovery reflects derived aggregates (mirrors calculate_staff_recovery_time)
    const staff_recovery_hours = (() => {
      const { total, minimum, ttm } = derivedStaffing;
      if (!total || !minimum) return 0;
      if (minimum >= total) return ttm;
      const ratio = minimum / total;
      const mult = ratio >= 0.75 ? 1.25 : ratio >= 0.5 ? 1.5 : 2.0;
      return ttm * mult;
    })();

    for (const band of bands) {
      for (const cat of categories) {
        if (matrix[cat]?.[band] === "high") {
          setCalculatedPreview({
            rto_hours: bandHours[band], rto_band: band,
            criticality_tier: bandToTier[band], rpo_hours: rpo_hours,
            staff_recovery_hours, rpo_source: strictestAssetRpo ? strictestAssetRpo.name : "process-level",
            trigger_category: cat.replace("_impact", ""),
            explanation: `RTO set to ${bandHours[band]}h because ${cat.replace("_impact", "")} impact reaches HIGH at ${band.replace("band_", "Band ")}`
          });
          return;
        }
      }
    }
    setCalculatedPreview({
      rto_hours: 168, rto_band: "band_4", criticality_tier: "tier_4",
      rpo_hours: rpo_hours, staff_recovery_hours, rpo_source: strictestAssetRpo ? strictestAssetRpo.name : "process-level",
      trigger_category: null,
      explanation: "No category reaches HIGH. RTO defaults to 72+ hours."
    });
  };

  const resetForm = () => {
    setFormData({ ...DEFAULT_FORM_DATA });
    setCurrentStep(1);
    setTempVendor("");
    setTempFacility("");
    setCalculatedPreview(null);
    setShowVendorForm(false);
    setShowFacilityForm(false);
    setNewVendor({ vendor_name: "", vendor_type: "technology", criticality: "medium", contact_name: "", contact_email: "", contact_phone: "", address: "", services_provided: "" });
    setNewFacility({ facility_name: "", facility_type: "primary", address: "", city: "", activation_time_hours: 0, capacity_percentage: 100, is_remote_capable: false });
    setCustomUpstream("");
    setCustomDownstream("");
    setStakeholderSearch("");
    setShowStakeholderDropdown(false);
  };

  const selectStakeholderAsOwner = (stakeholder) => {
    setFormData(prev => ({
      ...prev,
      process_owner_name: stakeholder.name,
      process_owner_email: stakeholder.email || "",
      process_owner_phone: stakeholder.phone || "",
      process_owner_stakeholder_id: stakeholder.id
    }));
    setStakeholderSearch("");
    setShowStakeholderDropdown(false);
    toast.success(`Selected ${stakeholder.name} as Process Owner`);
  };

  const filteredStakeholders = stakeholders.filter(s =>
    s.name?.toLowerCase().includes(stakeholderSearch.toLowerCase()) ||
    s.email?.toLowerCase().includes(stakeholderSearch.toLowerCase()) ||
    s.department?.toLowerCase().includes(stakeholderSearch.toLowerCase())
  );

  const handleImpactChange = (category, band, value) => {
    setFormData(prev => {
      const row = { ...prev[category] };
      const bandIdx = BAND_ORDER.indexOf(band);
      const newRank = IMPACT_RANK[value] ?? 0;
      row[band] = value;
      for (let i = bandIdx + 1; i < BAND_ORDER.length; i++) {
        if ((IMPACT_RANK[row[BAND_ORDER[i]]] ?? 0) < newRank) row[BAND_ORDER[i]] = value;
      }
      for (let i = bandIdx - 1; i >= 0; i--) {
        if ((IMPACT_RANK[row[BAND_ORDER[i]]] ?? 0) > newRank) row[BAND_ORDER[i]] = value;
      }
      return { ...prev, [category]: row };
    });
  };

  const addVendor = () => {
    if (tempVendor.trim() && !formData.critical_vendors.includes(tempVendor.trim())) {
      setFormData(prev => ({ ...prev, critical_vendors: [...prev.critical_vendors, tempVendor.trim()] }));
      setTempVendor("");
    }
  };
  const removeVendor = (index) => setFormData(prev => ({ ...prev, critical_vendors: prev.critical_vendors.filter((_, i) => i !== index) }));

  const addEnhancedVendor = () => {
    if (!newVendor.vendor_name.trim()) { toast.error("Vendor name is required"); return; }
    if (!newVendor.contact_email.trim()) { toast.error("Contact email is required for vendor RTO inheritance"); return; }
    const vendorObj = { ...newVendor, id: Date.now().toString(), services_provided: newVendor.services_provided.split(",").map(s => s.trim()).filter(Boolean) };
    setFormData(prev => ({ ...prev, vendor_dependencies: [...prev.vendor_dependencies, vendorObj] }));
    setNewVendor({ vendor_name: "", vendor_type: "technology", criticality: "medium", contact_name: "", contact_email: "", contact_phone: "", address: "", services_provided: "" });
    setShowVendorForm(false);
    toast.success("Vendor added - RTO will be inherited from this process");
  };

  // V22.9.8 — Quick-add a vendor dependency directly from a federated
  // connectivity link. Inherits criticality from the linked Application
  // Profile (if available), defaults vendor_type to 'service_provider',
  // leaves contact_email blank with a yellow needs-input chip so the user
  // can complete the record at submit time.
  const quickAddVendorFromConnectivity = (link) => {
    if (!link?.vendor_name) { toast.error("Connectivity row has no vendor name"); return; }
    const existing = (formData.vendor_dependencies || []).find(
      (v) => (v.vendor_name || "").toLowerCase() === link.vendor_name.toLowerCase(),
    );
    if (existing) {
      toast.info(`${link.vendor_name} is already in your vendor dependencies`);
      return;
    }
    const linkedApp = applicationProfiles.find(
      (a) => a.id === link.linked_application_id,
    );
    const inheritedCriticality = (linkedApp?.criticality || "medium").toLowerCase();
    const vendorObj = {
      id: Date.now().toString(),
      vendor_name: link.vendor_name,
      vendor_type: "service_provider",
      criticality: ["critical", "high", "medium", "low"].includes(inheritedCriticality)
        ? inheritedCriticality
        : "medium",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      address: "",
      services_provided: [link.protocol ? `${link.protocol} integration` : "Integration"],
      // V22.9.8 — provenance + completeness flag
      source_connectivity_link: {
        protocol: link.protocol,
        destination_ip: link.destination_ip,
        port: link.port,
        application_id: link.linked_application_id,
      },
      needs_contact_email: true,
    };
    setFormData(prev => ({
      ...prev,
      vendor_dependencies: [...(prev.vendor_dependencies || []), vendorObj],
    }));
    toast.success(`Added ${link.vendor_name} as Vendor Dep · Add contact email before saving`);
  };
  const removeEnhancedVendor = (id) => setFormData(prev => ({ ...prev, vendor_dependencies: prev.vendor_dependencies.filter(v => v.id !== id) }));

  const addFacility = () => {
    if (tempFacility.trim() && !formData.critical_facilities.includes(tempFacility.trim())) {
      setFormData(prev => ({ ...prev, critical_facilities: [...prev.critical_facilities, tempFacility.trim()] }));
      setTempFacility("");
    }
  };
  const removeFacility = (index) => setFormData(prev => ({ ...prev, critical_facilities: prev.critical_facilities.filter((_, i) => i !== index) }));

  const addEnhancedFacility = () => {
    if (!newFacility.facility_name.trim()) { toast.error("Facility name is required"); return; }
    const facilityObj = { ...newFacility, id: Date.now().toString() };
    setFormData(prev => ({ ...prev, facility_dependencies: [...prev.facility_dependencies, facilityObj] }));
    setNewFacility({ facility_name: "", facility_type: "primary", address: "", city: "", activation_time_hours: 0, capacity_percentage: 100, is_remote_capable: false });
    setShowFacilityForm(false);
    toast.success("Facility added - RTO will be inherited from this process");
  };
  const removeEnhancedFacility = (id) => setFormData(prev => ({ ...prev, facility_dependencies: prev.facility_dependencies.filter(f => f.id !== id) }));

  const addCustomUpstream = () => {
    if (customUpstream.trim() && !formData.custom_upstream_dependencies.includes(customUpstream.trim())) {
      setFormData(prev => ({ ...prev, custom_upstream_dependencies: [...prev.custom_upstream_dependencies, customUpstream.trim()] }));
      setCustomUpstream("");
      toast.success("Custom upstream dependency added");
    }
  };
  const removeCustomUpstream = (index) => setFormData(prev => ({ ...prev, custom_upstream_dependencies: prev.custom_upstream_dependencies.filter((_, i) => i !== index) }));

  const addCustomDownstream = () => {
    if (customDownstream.trim() && !formData.custom_downstream_dependencies.includes(customDownstream.trim())) {
      setFormData(prev => ({ ...prev, custom_downstream_dependencies: [...prev.custom_downstream_dependencies, customDownstream.trim()] }));
      setCustomDownstream("");
      toast.success("Custom downstream dependency added");
    }
  };
  const removeCustomDownstream = (index) => setFormData(prev => ({ ...prev, custom_downstream_dependencies: prev.custom_downstream_dependencies.filter((_, i) => i !== index) }));

  const toggleApplicationDependency = (appId) => {
    setFormData(prev => {
      const current = prev.application_dependencies || [];
      return { ...prev, application_dependencies: current.includes(appId) ? current.filter(id => id !== appId) : [...current, appId] };
    });
  };

  const addCustomAppDependency = () => {
    if (customAppDependency.trim() && !(formData.custom_application_dependencies || []).includes(customAppDependency.trim())) {
      setFormData(prev => ({ ...prev, custom_application_dependencies: [...(prev.custom_application_dependencies || []), customAppDependency.trim()] }));
      setCustomAppDependency("");
      toast.success("Custom application dependency added");
    }
  };
  const removeCustomAppDependency = (index) => setFormData(prev => ({ ...prev, custom_application_dependencies: (prev.custom_application_dependencies || []).filter((_, i) => i !== index) }));

  const toggleDependency = (type, processId) => {
    const key = type === "upstream" ? "upstream_process_ids" : "downstream_process_ids";
    const detailsKey = type === "upstream" ? "upstream_dependency_details" : "downstream_dependency_details";
    setFormData(prev => {
      const current = prev[key];
      const isRemoving = current.includes(processId);
      const newDetails = { ...prev[detailsKey] };
      if (isRemoving) {
        delete newDetails[processId];
      } else {
        newDetails[processId] = { dependency_type: "operational_output", criticality: "medium" };
      }
      return {
        ...prev,
        [key]: isRemoving ? current.filter(id => id !== processId) : [...current, processId],
        [detailsKey]: newDetails,
      };
    });
  };

  const updateDependencyDetail = (type, processId, field, value) => {
    const detailsKey = type === "upstream" ? "upstream_dependency_details" : "downstream_dependency_details";
    setFormData(prev => ({
      ...prev,
      [detailsKey]: {
        ...prev[detailsKey],
        [processId]: { ...(prev[detailsKey]?.[processId] || {}), [field]: value },
      },
    }));
  };

  const getRPOExplanation = () => {
    const impact = formData.data_loss_financial_impact;
    const hours = formData.data_loss_tolerance_hours;
    if (impact > 500000) return `Your RPO of ${Math.min(hours, 1)}h was derived from your high data loss impact ($${impact.toLocaleString()}). Financial impact >$500K requires max 1h RPO.`;
    if (impact > 100000) return `Your RPO of ${Math.min(hours, 4)}h was derived from your data loss impact ($${impact.toLocaleString()}). Financial impact >$100K requires max 4h RPO.`;
    if (impact > 50000) return `Your RPO of ${Math.min(hours, 8)}h was derived from your data loss impact ($${impact.toLocaleString()}). Financial impact >$50K requires max 8h RPO.`;
    return `Your RPO of ${Math.min(hours, 24)}h is based on your data loss tolerance. Lower financial impact allows longer recovery point.`;
  };

  const getStaffRTExplanation = () => {
    const total_staff = derivedStaffing.total;
    const minimum_staff = derivedStaffing.minimum;
    const time_to_min_staff_hours = derivedStaffing.ttm;
    if (total_staff === 0 || minimum_staff === 0) return "Staff recovery time not calculated (staff numbers not provided).";
    const ratio = minimum_staff / total_staff;
    const multiplier = ratio >= 0.75 ? 1.25 : ratio >= 0.5 ? 1.5 : 2.0;
    const staffRT = time_to_min_staff_hours * multiplier;
    return `Staff RT of ${staffRT.toFixed(1)}h: ${minimum_staff}/${total_staff} min staff (${(ratio * 100).toFixed(0)}% ratio) \u00D7 ${time_to_min_staff_hours}h base time \u00D7 ${multiplier} multiplier.`;
  };

  // ── D1 (per-role staffing) helpers ──
  const staffingRoles = formData.staffing_roles || [];
  const rolesActive = staffingRoles.some(r => (r.total || 0) > 0 || (r.minimum || 0) > 0);
  const derivedStaffing = (() => {
    if (!rolesActive) return { total: formData.total_staff, minimum: formData.minimum_staff, ttm: formData.time_to_min_staff_hours };
    const total = staffingRoles.reduce((s, r) => s + (parseInt(r.total) || 0), 0);
    const minimum = staffingRoles.reduce((s, r) => s + (parseInt(r.minimum) || 0), 0);
    const times = staffingRoles.filter(r => (r.minimum || 0) > 0).map(r => parseFloat(r.time_to_min_hours) || 0);
    return { total, minimum, ttm: times.length ? Math.max(...times) : 0 };
  })();
  const addStaffingRole = () => setFormData(prev => ({ ...prev, staffing_roles: [...(prev.staffing_roles || []), { role: "", total: 0, minimum: 0, time_to_min_hours: 0, remote_capable: false }] }));
  const updateStaffingRole = (i, field, val) => setFormData(prev => { const arr = [...(prev.staffing_roles || [])]; arr[i] = { ...arr[i], [field]: val }; return { ...prev, staffing_roles: arr }; });
  const removeStaffingRole = (i) => setFormData(prev => ({ ...prev, staffing_roles: (prev.staffing_roles || []).filter((_, idx) => idx !== i) }));

  // ── D2 (per-asset data sensitivity) helpers ──
  const protectionHint = (rpo) => rpo == null ? null : rpo <= 1 ? "continuous / synchronous replication" : rpo <= 4 ? "async replication or hourly snapshots" : rpo <= 8 ? "intra-day incremental backups" : "daily backup cycle";
  const rpoFromDataLoss = (tolerance, impact) => { // mirrors backend calculate_rpo_from_data_loss for live preview only
    const t = parseFloat(tolerance) || 24; const f = parseFloat(impact) || 0;
    const cap = f > 500000 ? 1 : f > 100000 ? 4 : f > 50000 ? 8 : 24;
    return Math.min(t, cap);
  };
  const sensitivityList = formData.asset_data_sensitivity || [];
  const selectedAssetsForSensitivity = (() => {
    // union of selected application profiles + custom app deps (as equipment/app refs)
    const apps = (formData.application_dependencies || []).map(id => {
      const p = (applicationProfiles || []).find(a => a.id === id);
      return { asset_ref: id, asset_type: "application", asset_name: p ? (p.application_name || p.name || id) : id };
    });
    const customs = (formData.custom_application_dependencies || []).map(nm => ({ asset_ref: nm, asset_type: "equipment", asset_name: nm }));
    return [...apps, ...customs];
  })();
  const getSensitivity = (ref) => sensitivityList.find(s => s.asset_ref === ref) || null;
  const setSensitivity = (asset, patch) => setFormData(prev => {
    const arr = [...(prev.asset_data_sensitivity || [])];
    const idx = arr.findIndex(s => s.asset_ref === asset.asset_ref);
    if (idx >= 0) arr[idx] = { ...arr[idx], ...patch };
    else arr.push({ asset_ref: asset.asset_ref, asset_type: asset.asset_type, asset_name: asset.asset_name, holds_sensitive_data: false, data_loss_tolerance_hours: null, data_loss_financial_impact: null, ...patch });
    return { ...prev, asset_data_sensitivity: arr };
  });
  const strictestAssetRpo = (() => {
    const sensed = sensitivityList.filter(s => s.holds_sensitive_data && s.data_loss_tolerance_hours != null);
    if (!sensed.length) return null;
    let best = null;
    sensed.forEach(s => { const r = rpoFromDataLoss(s.data_loss_tolerance_hours, s.data_loss_financial_impact); if (best == null || r < best.rpo) best = { rpo: r, name: s.asset_name || s.asset_ref }; });
    return best;
  })();


  const getRTOConflicts = () => {
    if (!calculatedPreview?.rto_hours) return [];
    const currentRTO = calculatedPreview.rto_hours;
    const conflicts = [];
    for (const procId of formData.upstream_process_ids) {
      const proc = availableProcesses.find(p => p.id === procId);
      if (proc && proc.calculated_rto_hours > currentRTO) {
        conflicts.push({
          processId: procId, processName: proc.process_name,
          upstreamRTO: proc.calculated_rto_hours, currentRTO: currentRTO,
          gap: proc.calculated_rto_hours - currentRTO,
          severity: (proc.calculated_rto_hours - currentRTO) > 24 ? "high" : "medium"
        });
      }
    }
    return conflicts;
  };

  const getConflictDetails = (procId) => {
    if (!calculatedPreview?.rto_hours) return null;
    const proc = availableProcesses.find(p => p.id === procId);
    if (!proc || proc.calculated_rto_hours <= calculatedPreview.rto_hours) return null;
    return { upstreamRTO: proc.calculated_rto_hours, currentRTO: calculatedPreview.rto_hours, gap: proc.calculated_rto_hours - calculatedPreview.rto_hours };
  };

  // V22.9 — when the wizard opens in technical mode, pre-set classification fields
  useEffect(() => {
    if (!open) return;
    if (mode === "technical") {
      setFormData(prev => ({
        ...prev,
        process_category: "it_technical",
        is_it_process: true,
        department: lockedDepartment || prev.department || "Information Technology",
        asset_types: prev.asset_types && prev.asset_types.length > 0 ? prev.asset_types : ["it_infrastructure"],
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        process_category: "business",
        is_it_process: false,
        it_function_scope: "",
      }));
    }
  }, [open, mode, lockedDepartment]);

  // Pre-fill from an existing draft (Complete Assessment shortcut)
  useEffect(() => {
    if (!open || !prefill) return;
    setFormData(prev => ({
      ...prev,
      process_name: prefill.process_name || prev.process_name,
      department: prefill.department || prev.department,
      description: prefill.description || prev.description,
    }));
    // Ensure the prefilled department is selectable in the dropdown even if it
    // isn't one of the org's configured departments (onboarding seed may use
    // free-form names like "Operations" or "Legal/Compliance").
    if (prefill.department) {
      setOrgDepartments(prev => (prev.includes(prefill.department) ? prev : [prefill.department, ...prev]));
    }
  }, [open, prefill]);

  // Task 0 — hydrate the full form when editing an existing assessment
  useEffect(() => {
    if (!open || !editProcess) return;
    const e = editProcess;
    const im = e.impact_matrix || {};
    setFormData(prev => ({
      ...prev,
      ...e,
      department: e.department || e.business_unit || prev.department,
      // impact matrix rows are stored nested; lift them to top-level form fields
      financial_impact: im.financial_impact || prev.financial_impact,
      legal_impact: im.legal_impact || prev.legal_impact,
      regulatory_impact: im.regulatory_impact || prev.regulatory_impact,
      reputational_impact: im.reputational_impact || prev.reputational_impact,
      operational_impact: im.operational_impact || prev.operational_impact,
      staffing_roles: e.staffing_roles || [],
      asset_data_sensitivity: e.asset_data_sensitivity || [],
    }));
    if (e.department || e.business_unit) {
      const d = e.department || e.business_unit;
      setOrgDepartments(prev => (prev.includes(d) ? prev : [d, ...prev]));
    }
  }, [open, editProcess]);


  const handleSubmit = async () => {
    if (!formData.process_name || !formData.department) {
      toast.error("Please fill in Process Name and Department");
      return;
    }
    if (mode === "technical" && !formData.it_function_scope) {
      toast.error("Please select an IT Function Scope (Network / Database / Cloud / Security / Identity / Messaging / ERP Backbone / Other)");
      return;
    }
    // V22.9.8 — Block submit if any quick-added vendor still lacks a contact email
    const incompleteQuickAdds = (formData.vendor_dependencies || []).filter(
      (v) => v.needs_contact_email && !v.contact_email,
    );
    if (incompleteQuickAdds.length > 0) {
      toast.error(
        `${incompleteQuickAdds.length} quick-added vendor${incompleteQuickAdds.length > 1 ? "s" : ""} need a contact email before saving: ${incompleteQuickAdds.map((v) => v.vendor_name).join(", ")}`,
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        // Backend expects business_unit, not department
        business_unit: formData.department,
        process_category: mode === "technical" ? "it_technical" : "business",
        is_it_process: mode === "technical",
        it_function_scope: mode === "technical" ? formData.it_function_scope : null,
        // D1/D2 (Phase 2) — pass through per-role staffing & per-asset sensitivity
        staffing_roles: (formData.staffing_roles && formData.staffing_roles.length) ? formData.staffing_roles : null,
        asset_data_sensitivity: (formData.asset_data_sensitivity && formData.asset_data_sensitivity.length) ? formData.asset_data_sensitivity : null,
      };
      // Task 0 — edit an existing assessment via the existing PUT (reopen-on-edit fires)
      const isEdit = !!(editProcess && editProcess.id);
      const res = isEdit
        ? await api.put(`/bia-engine/processes/${editProcess.id}`, payload)
        : await api.post("/bia-engine/processes", payload);
      // E2 — if a save returned an assessment to Pending Review, surface it
      if (res?.data?.reopened) {
        toast.warning("This assessment was approved — it has returned to Pending Review and the program sign-off now requires re-signature.");
      } else {
        toast.success(isEdit ? "Assessment updated successfully!" : (mode === "technical" ? "Technical BIA created successfully!" : "Process BIA created successfully!"));
      }
      onOpenChange(false);
      resetForm();
      if (onSuccess) await onSuccess();
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.includes("Trial limit")) {
        triggerUpgradeWall("Bia Process", processCount, 5);
      } else {
        toast.error(error.response?.data?.detail || "Failed to save Process BIA");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={mode === "technical" ? "bia-wizard-technical" : "bia-wizard-business"}>
        <DialogHeader>
          <DialogTitle>{editProcess?.id
            ? (mode === "technical" ? "Edit Technical BIA Assessment" : "Edit Process BIA Assessment")
            : (mode === "technical" ? "New Technical BIA Assessment" : "New Process BIA Assessment")}</DialogTitle>
          <DialogDescription>
            {mode === "technical"
              ? "For backbone infrastructure or enterprise-level applications (e.g. Core Networking, Active Directory, Global ERP) that don't map to a single business process. Uses the same 5x4 Impact Matrix as Business BIAs."
              : "Analyze business process impact using the 5x4 matrix to calculate RTO and RPO"}
          </DialogDescription>
        </DialogHeader>

        {editProcess?.edit_fallback && (
          <div
            className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800"
            data-testid="edit-fallback-banner"
          >
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-sky-600" />
            <span>You can edit this BIA because no Process Manager or Department Head is assigned. This edit is recorded in the audit log.</span>
          </div>
        )}

        {/* Wizard Steps */}
        <div className="flex items-center justify-between mb-6 px-2 bg-slate-50 rounded-lg p-3 border border-slate-200">
          {WIZARD_STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex flex-col items-center p-2 rounded-lg transition-all ${
                    isActive ? "bg-white shadow-sm border border-sky-200" : "hover:bg-white/50"
                  }`}
                  data-testid={`wizard-step-${step.id}`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center mb-1.5 text-sm font-extrabold transition-all ${
                      isActive ? "bg-sky-700 text-white shadow-md shadow-sky-700/25" :
                      isCompleted ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500"
                    }`}
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : step.id}
                  </div>
                  <span className={`text-[0.6875rem] font-medium ${isActive ? "text-sky-700" : isCompleted ? "text-emerald-600" : "text-slate-400"}`}>
                    {step.title}
                  </span>
                </button>
                {index < WIZARD_STEPS.length - 1 && (
                  <div className={`flex-1 h-[3px] mx-1 rounded-full transition-all ${isCompleted ? "bg-emerald-400" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: Process Info */}
        {currentStep === 1 && (
          <div className="space-y-4" data-testid="step-process-info">
            {mode === "technical" && (
              <div className="rounded-lg border-2 border-violet-300 bg-gradient-to-r from-violet-50 via-white to-sky-50 p-4" data-testid="bia-wizard-mode-banner">
                <div className="flex items-start gap-3">
                  <Network className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-violet-900 mb-0.5">Technical BIA · V22.9</div>
                    <p className="text-slate-700">
                      You are creating a Technical BIA for backbone infrastructure (e.g. Core Networking, Active Directory, ERP backbone). The 5&times;4 Impact Matrix, RTO/RPO calculator, and BIA-to-Recovery cascade are <strong>identical</strong> to a Business BIA. Approval flow: <strong>you (1st-level)</strong> &rarr; IT-line Department Head (final).
                    </p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1">
                    <Label className="text-xs">IT Function Scope *</Label>
                    <Select value={formData.it_function_scope} onValueChange={(v) => setFormData({ ...formData, it_function_scope: v })}>
                      <SelectTrigger data-testid="select-it-function-scope" className="bg-white"><SelectValue placeholder="Select scope" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="network">Network (LAN/WAN/SD-WAN)</SelectItem>
                        <SelectItem value="database">Database (RDBMS / NoSQL / Warehouse)</SelectItem>
                        <SelectItem value="cloud">Cloud Platform (IaaS / PaaS)</SelectItem>
                        <SelectItem value="security">Security (IAM-adjacent, EDR, SIEM)</SelectItem>
                        <SelectItem value="identity">Identity Provider (AD / Entra / Okta)</SelectItem>
                        <SelectItem value="messaging">Messaging / Collaboration (Exchange / Teams / O365)</SelectItem>
                        <SelectItem value="erp_backbone">ERP Backbone (Oracle / SAP / Workday)</SelectItem>
                        <SelectItem value="other">Other Enterprise Service</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Department (locked to IT-line)</Label>
                    <Input value={formData.department || "Information Technology"} disabled className="bg-slate-50 text-slate-700" data-testid="locked-it-department" />
                  </div>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Process Name *</Label>
                <Input placeholder={mode === "technical" ? "e.g., Core Networking Backbone" : "e.g., Order Processing System"} value={formData.process_name} onChange={(e) => setFormData({ ...formData, process_name: e.target.value })} data-testid="input-process-name" />
              </div>
              <div className="space-y-2">
                <Label>Department *</Label>
                <Select value={formData.department} onValueChange={(value) => setFormData({ ...formData, department: value })} disabled={mode === "technical"}>
                  <SelectTrigger data-testid="select-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    {(orgDepartments.length > 0 ? orgDepartments : FALLBACK_DEPARTMENTS).map((dept) => (<SelectItem key={dept} value={dept}>{dept}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* AI RTO/RPO Suggest */}
            <div className="rounded-lg border border-purple-200 bg-gradient-to-r from-purple-50 via-white to-sky-50 p-3" data-testid="ai-rto-suggest-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span><strong>AI Suggest</strong> — let AI recommend Tier, RTO &amp; RPO based on your inputs.</span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={runAiSuggest}
                  disabled={aiSuggesting || !formData.process_name}
                  className="bg-purple-600 hover:bg-purple-700 gap-2"
                  data-testid="ai-rto-suggest-btn"
                >
                  {aiSuggesting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</> : <><Sparkles className="w-3.5 h-3.5" /> Ask AI</>}
                </Button>
              </div>
              {aiSuggestion && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 items-stretch">
                  <div className="rounded bg-white border border-purple-200 px-2 py-1.5 text-center">
                    <div className="text-[10px] text-slate-500">Tier</div>
                    <div className="text-lg font-bold text-purple-700">{aiSuggestion.tier}</div>
                  </div>
                  <div className="rounded bg-white border border-purple-200 px-2 py-1.5 text-center">
                    <div className="text-[10px] text-slate-500">RTO</div>
                    <div className="text-lg font-bold text-purple-700">{aiSuggestion.rto_hours}h</div>
                  </div>
                  <div className="rounded bg-white border border-purple-200 px-2 py-1.5 text-center">
                    <div className="text-[10px] text-slate-500">RPO</div>
                    <div className="text-lg font-bold text-purple-700">{aiSuggestion.rpo_hours}h</div>
                  </div>
                  <div className="rounded bg-white border border-purple-200 px-2 py-1.5 text-center">
                    <div className="text-[10px] text-slate-500">Confidence</div>
                    <div className="text-lg font-bold text-purple-700">{Math.round((aiSuggestion.confidence || 0) * 100)}%</div>
                  </div>
                  <div className="col-span-2 md:col-span-4 text-[12px] text-slate-600 italic pt-1 border-t border-purple-100">
                    <Sparkles className="inline w-3 h-3 mr-1 text-purple-500" />
                    {aiSuggestion.reasoning}
                  </div>
                </div>
              )}
            </div>

            {/* Smart Process Owner Selector */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-400" /> Process Owner
                <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700">Smart Select</Badge>
              </Label>
              <div className="relative">
                {formData.process_owner_stakeholder_id ? (
                  <div className="flex items-center justify-between p-3 border rounded-lg bg-sky-50 border-sky-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-sky-700 text-white flex items-center justify-center font-semibold">
                        {formData.process_owner_name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{formData.process_owner_name}</p>
                        <p className="text-sm text-slate-500">{formData.process_owner_email || "No email"}{formData.process_owner_phone && ` \u2022 ${formData.process_owner_phone}`}</p>
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setFormData(prev => ({ ...prev, process_owner_name: "", process_owner_email: "", process_owner_phone: "", process_owner_stakeholder_id: null }))} className="text-slate-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      placeholder="Search stakeholders or type name..."
                      value={stakeholderSearch || formData.process_owner_name}
                      onChange={(e) => { const value = e.target.value; setStakeholderSearch(value); setFormData(prev => ({ ...prev, process_owner_name: value })); setShowStakeholderDropdown(value.length > 0 && stakeholders.length > 0); }}
                      onFocus={() => { if (stakeholders.length > 0) setShowStakeholderDropdown(true); }}
                      data-testid="process-owner-input"
                    />
                    {showStakeholderDropdown && filteredStakeholders.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border rounded-lg shadow-lg">
                        <div className="p-2 text-xs text-slate-500 bg-slate-50 border-b">Select from Stakeholder Register or continue typing</div>
                        {filteredStakeholders.slice(0, 10).map((stakeholder) => (
                          <button key={stakeholder.id} type="button" onClick={() => selectStakeholderAsOwner(stakeholder)} className="w-full p-3 text-left hover:bg-sky-50 flex items-center gap-3 border-b last:border-0">
                            <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-sm font-medium">{stakeholder.name?.charAt(0)?.toUpperCase() || "?"}</div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-slate-900 truncate">{stakeholder.name}</p>
                              <p className="text-xs text-slate-500 truncate">{stakeholder.email || "No email"}{stakeholder.type && ` \u2022 ${stakeholder.type}`}</p>
                            </div>
                            <Badge variant="outline" className="text-xs flex-shrink-0">{stakeholder.type || "internal"}</Badge>
                          </button>
                        ))}
                        {filteredStakeholders.length > 10 && (<div className="p-2 text-xs text-slate-500 text-center bg-slate-50">+{filteredStakeholders.length - 10} more results</div>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {stakeholders.length === 0 && (<p className="text-xs text-slate-500 flex items-center gap-1"><Info className="w-3 h-3" />No stakeholders in register. You can still type a name manually.</p>)}
              {stakeholders.length > 0 && !formData.process_owner_stakeholder_id && (<p className="text-xs text-sky-700 flex items-center gap-1"><Users className="w-3 h-3" />{stakeholders.length} stakeholders available - select to auto-fill contact details</p>)}
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea placeholder="Describe the business process..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
            </div>

            {/* Asset Type Classification */}
            <div className="space-y-3 mt-4">
              <Label className="flex items-center gap-2">
                <Target className="w-4 h-4 text-slate-400" /> Asset Type Classification
                <span className="text-xs text-slate-500 ml-2">(Select all that apply - used for Risk Assessment threat filtering)</span>
              </Label>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg">
                {ASSET_TYPE_OPTIONS.map((assetType) => {
                  const isSelected = formData.asset_types.includes(assetType.id);
                  return (
                    <div key={assetType.id} onClick={() => setFormData(prev => ({ ...prev, asset_types: isSelected ? prev.asset_types.filter(t => t !== assetType.id) : [...prev.asset_types, assetType.id] }))}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? "border-sky-700 bg-sky-50 ring-1 ring-sky-700" : "border-slate-200 hover:border-sky-300 bg-white"}`}
                      data-testid={`asset-type-${assetType.id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{assetType.icon}</span>
                        <div className="flex-1"><p className="font-medium text-sm">{assetType.name}</p><p className="text-xs text-slate-500">{assetType.description}</p></div>
                        {isSelected && <CheckCircle2 className="w-5 h-5 text-sky-700 flex-shrink-0" />}
                      </div>
                    </div>
                  );
                })}
              </div>
              {formData.asset_types.length > 0 && (<p className="text-xs text-sky-700">{formData.asset_types.length} asset type(s) selected - Risk assessments will filter threats based on these categories</p>)}
            </div>

            {/* MBCO */}
            <div className="space-y-3 mt-4 p-4 border-2 border-dashed rounded-lg border-emerald-300 bg-emerald-50">
              <Label className="flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-800 font-semibold">Minimum Business Continuity Objective (MBCO)</span>
                <Badge variant="outline" className="text-xs">ISO 8.2.2</Badge>
              </Label>
              <p className="text-sm text-emerald-700">Define the minimum acceptable level of service during recovery (e.g., "50% of normal capacity")</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-emerald-800">MBCO Percentage</Label>
                  <div className="flex items-center gap-3">
                    <input type="range" min="10" max="100" step="10" value={formData.mbco_percentage || 50} onChange={(e) => setFormData({ ...formData, mbco_percentage: parseInt(e.target.value) })} className="flex-1" />
                    <span className="font-mono text-lg font-semibold text-emerald-700 w-16 text-right">{formData.mbco_percentage || 50}%</span>
                  </div>
                  <p className="text-xs text-emerald-600">Minimum {formData.mbco_percentage || 50}% of normal operations must be restored within RTO</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-emerald-800">MBCO Description</Label>
                  <Textarea placeholder="Describe what minimum acceptable service looks like..." value={formData.mbco_description || ""} onChange={(e) => setFormData({ ...formData, mbco_description: e.target.value })} rows={2} className="bg-white" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Impact Matrix */}
        {currentStep === 2 && (
          <div className="space-y-4" data-testid="step-impact-matrix">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800"><strong>5x4 Impact Matrix:</strong> Rate the impact level (No/Low/Medium/High) for each category across the 4 time bands. RTO is set to the first time band where any category reaches &quot;High&quot;.</p>
            </div>
            {calculatedPreview && (
              <div className={`p-4 rounded-lg border-2 ${getRTOBandColor(calculatedPreview.rto_band)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className="w-6 h-6" />
                    <div>
                      <div className="flex items-center gap-4">
                        <span className="font-semibold">Calculated RTO: {calculatedPreview.rto_hours} hours</span>
                        <Badge className="bg-white/50">{formatTier(calculatedPreview.criticality_tier).toUpperCase()}</Badge>
                        <span className="text-sm opacity-75">RPO: {calculatedPreview.rpo_hours}h</span>
                      </div>
                      <p className="text-sm opacity-75">{calculatedPreview.explanation}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40"><div className="flex items-center gap-1">Category<span className="text-xs text-muted-foreground">(hover for definitions)</span></div></TableHead>
                    {TIME_BANDS.map((band) => (<TableHead key={band.id} className="text-center"><div className="flex flex-col items-center"><span className="font-semibold">{band.name}</span><span className="text-xs text-slate-500">{band.label}</span></div></TableHead>))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {IMPACT_CATEGORIES.map((category) => {
                    const categoryKey = category.key.replace("_impact", "");
                    return (
                      <TableRow key={category.key}>
                        <TableCell>
                          <ImpactCategoryHoverCard category={categoryKey}>
                            <div className="flex items-center gap-2 cursor-help">
                              <div><p className="font-medium">{category.label}</p><p className="text-xs text-slate-500">{category.description}</p></div>
                              <HelpCircle className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </ImpactCategoryHoverCard>
                        </TableCell>
                        {TIME_BANDS.map((band) => {
                          const currentValue = formData[category.key]?.[band.id] || "no_impact";
                          return (
                            <TableCell key={band.id} className="p-1">
                              <div className="flex items-center gap-1">
                                <Select value={currentValue} onValueChange={(value) => handleImpactChange(category.key, band.id, value)}>
                                  <SelectTrigger className={`w-full ${IMPACT_LEVELS.find(l => l.value === currentValue)?.color || ""}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{IMPACT_LEVELS.map((level) => (<SelectItem key={level.value} value={level.value}><span className={`px-2 py-1 rounded ${level.color}`}>{level.label}</span></SelectItem>))}</SelectContent>
                                </Select>
                                <ImpactInfoButton category={categoryKey} level={currentValue} />
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Step 3: Dependencies */}
        {currentStep === 3 && (
          <div className="space-y-6" data-testid="step-dependencies">
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800"><strong>Self-Healing Dependencies:</strong> If an upstream process has a longer RTO than this process, a &quot;RTO Mismatch&quot; gap will be created. The upstream process owner will review and approve the auto-alignment.</p>
            </div>
            {getRTOConflicts().length > 0 && (
              <div className="p-4 bg-red-50 rounded-lg border-2 border-red-300" data-testid="rto-conflict-alert">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800 mb-2">RTO Conflict Detected ({getRTOConflicts().length})</p>
                    <p className="text-sm text-red-700 mb-3">The following upstream processes have longer RTOs than your process ({calculatedPreview?.rto_hours}h). This will create dependency gaps that require BCM Manager review.</p>
                    <div className="space-y-2">
                      {getRTOConflicts().map((conflict) => (
                        <div key={conflict.processId} className="flex items-center justify-between p-2 bg-white rounded border border-red-200">
                          <div><p className="font-medium text-sm text-red-900">{conflict.processName}</p><p className="text-xs text-red-600">Upstream RTO: {conflict.upstreamRTO}h &rarr; Gap: +{conflict.gap}h</p></div>
                          <Badge variant={conflict.severity === "high" ? "destructive" : "warning"} className="text-xs">{conflict.severity.toUpperCase()}</Badge>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-red-600 mt-3 italic">You can proceed, but the upstream process owner will be notified to reduce their RTO to {calculatedPreview?.rto_hours}h or less.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Upstream Dependencies */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-slate-400" />Upstream Dependencies (processes this depends on){calculatedPreview?.rto_hours && <span className="text-xs text-slate-500 ml-2">(Your RTO: {calculatedPreview.rto_hours}h)</span>}</Label>
              <div className="space-y-2 max-h-[420px] overflow-y-auto p-2 bg-slate-50 rounded-lg">
                {availableProcesses.length === 0 ? (<p className="text-sm text-slate-500 text-center py-4">No other processes available</p>) : (
                  availableProcesses.map((proc) => {
                    const isSelected = formData.upstream_process_ids.includes(proc.id);
                    const conflict = isSelected ? getConflictDetails(proc.id) : null;
                    const wouldConflict = !isSelected && calculatedPreview?.rto_hours && proc.calculated_rto_hours > calculatedPreview.rto_hours;
                    const details = formData.upstream_dependency_details?.[proc.id];
                    const depType = isSelected ? DEPENDENCY_TYPES.find(t => t.id === details?.dependency_type) : null;
                    const depCrit = isSelected ? DEPENDENCY_CRITICALITY.find(c => c.id === details?.criticality) : null;
                    return (
                      <div key={proc.id}
                        className={`rounded-lg border transition-all ${isSelected && conflict ? "border-red-400 bg-red-50" : isSelected ? "border-sky-700 bg-sky-50" : wouldConflict ? "border-amber-300 hover:border-amber-400 bg-amber-50/50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                        <div className="p-3 cursor-pointer" onClick={() => toggleDependency("upstream", proc.id)} data-testid={`upstream-dep-${proc.id}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{proc.process_name}</p>
                              <p className="text-xs text-slate-500">{proc.department} &bull; RTO: {proc.calculated_rto_hours}h</p>
                              {isSelected && conflict && <p className="text-xs text-red-600 mt-1 font-medium">RTO conflict: +{conflict.gap}h gap</p>}
                              {wouldConflict && !isSelected && <p className="text-xs text-amber-600 mt-1">Will create +{proc.calculated_rto_hours - calculatedPreview.rto_hours}h gap</p>}
                            </div>
                            <div className="flex items-center gap-2">
                              {isSelected && depType && (
                                <Badge variant="outline" className="text-[10px] gap-1" style={{ borderColor: depType.color, color: depType.color }}>
                                  {depType.label}
                                </Badge>
                              )}
                              {isSelected && depCrit && (
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: depCrit.color }} title={depCrit.label} />
                              )}
                              {isSelected && conflict ? <AlertTriangle className="w-5 h-5 text-red-500" /> : isSelected ? <CheckCircle2 className="w-5 h-5 text-sky-700" /> : null}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="px-3 pb-3 pt-1 border-t border-sky-200 space-y-2" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Dependency Type</label>
                                <Select value={details?.dependency_type || "operational_output"} onValueChange={(v) => updateDependencyDetail("upstream", proc.id, "dependency_type", v)}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`upstream-type-${proc.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DEPENDENCY_TYPES.map(dt => (
                                      <SelectItem key={dt.id} value={dt.id} className="text-xs">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dt.color }} />{dt.label}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Criticality</label>
                                <Select value={details?.criticality || "medium"} onValueChange={(v) => updateDependencyDetail("upstream", proc.id, "criticality", v)}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`upstream-crit-${proc.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DEPENDENCY_CRITICALITY.map(dc => (
                                      <SelectItem key={dc.id} value={dc.id} className="text-xs">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dc.color }} />{dc.label} &mdash; {dc.desc}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-sm text-blue-800 mb-2 block">Add External/Other Upstream Dependency</Label>
                <div className="flex gap-2">
                  <Input placeholder="e.g., External Payment Gateway, Third-party API" value={customUpstream} onChange={(e) => setCustomUpstream(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomUpstream())} className="flex-1" />
                  <Button type="button" variant="outline" size="sm" onClick={addCustomUpstream}><Plus className="w-4 h-4 mr-1" />Add</Button>
                </div>
                {formData.custom_upstream_dependencies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">{formData.custom_upstream_dependencies.map((dep, idx) => (<Badge key={idx} variant="secondary" className="gap-1 bg-blue-100">{dep}<button onClick={() => removeCustomUpstream(idx)} className="ml-1 hover:text-red-500">&times;</button></Badge>))}</div>
                )}
              </div>
              {/* V22.9.8 — Federated Upstream from selected Application Profiles */}
              <FederatedProcessDepsSurfaceImpl
                api={api}
                applicationIds={formData.application_dependencies || []}
                direction="upstream"
                alreadySelected={formData.custom_upstream_dependencies || []}
                onQuickAdd={(name) => {
                  if (!name) return;
                  if ((formData.custom_upstream_dependencies || []).some((x) => (x || "").toLowerCase() === name.toLowerCase())) {
                    toast.info(`${name} is already in upstream dependencies`);
                    return;
                  }
                  setFormData((prev) => ({ ...prev, custom_upstream_dependencies: [...(prev.custom_upstream_dependencies || []), name] }));
                  toast.success(`Added "${name}" as upstream dependency`);
                }}
              />
            </div>

            {/* Downstream Dependencies */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2"><Link2 className="w-4 h-4 text-slate-400" />Downstream Dependencies (processes that depend on this)</Label>
              <div className="space-y-2 max-h-[420px] overflow-y-auto p-2 bg-slate-50 rounded-lg">
                {availableProcesses.length === 0 ? (<p className="text-sm text-slate-500 text-center py-4">No other processes available</p>) : (
                  availableProcesses.map((proc) => {
                    const isSelected = formData.downstream_process_ids.includes(proc.id);
                    const details = formData.downstream_dependency_details?.[proc.id];
                    const depType = isSelected ? DEPENDENCY_TYPES.find(t => t.id === details?.dependency_type) : null;
                    const depCrit = isSelected ? DEPENDENCY_CRITICALITY.find(c => c.id === details?.criticality) : null;
                    return (
                      <div key={proc.id}
                        className={`rounded-lg border transition-all ${isSelected ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                        <div className="p-3 cursor-pointer" onClick={() => toggleDependency("downstream", proc.id)} data-testid={`downstream-dep-${proc.id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{proc.process_name}</p>
                              <p className="text-xs text-slate-500">{proc.department} &bull; RTO: {proc.calculated_rto_hours}h</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {isSelected && depType && (
                                <Badge variant="outline" className="text-[10px] gap-1" style={{ borderColor: depType.color, color: depType.color }}>
                                  {depType.label}
                                </Badge>
                              )}
                              {isSelected && depCrit && (
                                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: depCrit.color }} title={depCrit.label} />
                              )}
                              {isSelected && <CheckCircle2 className="w-5 h-5 text-purple-600" />}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <div className="px-3 pb-3 pt-1 border-t border-purple-200 space-y-2" onClick={(e) => e.stopPropagation()}>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Dependency Type</label>
                                <Select value={details?.dependency_type || "operational_output"} onValueChange={(v) => updateDependencyDetail("downstream", proc.id, "dependency_type", v)}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`downstream-type-${proc.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DEPENDENCY_TYPES.map(dt => (
                                      <SelectItem key={dt.id} value={dt.id} className="text-xs">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dt.color }} />{dt.label}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Criticality</label>
                                <Select value={details?.criticality || "medium"} onValueChange={(v) => updateDependencyDetail("downstream", proc.id, "criticality", v)}>
                                  <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`downstream-crit-${proc.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {DEPENDENCY_CRITICALITY.map(dc => (
                                      <SelectItem key={dc.id} value={dc.id} className="text-xs">
                                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dc.color }} />{dc.label} &mdash; {dc.desc}</span>
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <Label className="text-sm text-purple-800 mb-2 block">Add External/Other Downstream Dependency</Label>
                <div className="flex gap-2">
                  <Input placeholder="e.g., Customer Portal, Partner Integration" value={customDownstream} onChange={(e) => setCustomDownstream(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomDownstream())} className="flex-1" />
                  <Button type="button" variant="outline" size="sm" onClick={addCustomDownstream}><Plus className="w-4 h-4 mr-1" />Add</Button>
                </div>
                {formData.custom_downstream_dependencies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">{formData.custom_downstream_dependencies.map((dep, idx) => (<Badge key={idx} variant="secondary" className="gap-1 bg-purple-100">{dep}<button onClick={() => removeCustomDownstream(idx)} className="ml-1 hover:text-red-500">&times;</button></Badge>))}</div>
                )}
              </div>
              {/* V22.9.8 — Federated Downstream from selected Application Profiles */}
              <FederatedProcessDepsSurfaceImpl
                api={api}
                applicationIds={formData.application_dependencies || []}
                direction="downstream"
                alreadySelected={formData.custom_downstream_dependencies || []}
                onQuickAdd={(name) => {
                  if (!name) return;
                  if ((formData.custom_downstream_dependencies || []).some((x) => (x || "").toLowerCase() === name.toLowerCase())) {
                    toast.info(`${name} is already in downstream dependencies`);
                    return;
                  }
                  setFormData((prev) => ({ ...prev, custom_downstream_dependencies: [...(prev.custom_downstream_dependencies || []), name] }));
                  toast.success(`Added "${name}" as downstream dependency`);
                }}
              />
            </div>

            {/* Business Application Dependencies */}
            <div className="space-y-3 mt-6 pt-6 border-t">
              <Label className="flex items-center gap-2 text-base font-semibold"><Network className="w-4 h-4 text-cyan-600" />Upstream Dependencies (Critical Business Applications)<Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-700">From Application Profiles</Badge></Label>
              <p className="text-sm text-slate-600">Select business applications this process depends on. These inherit your process's RTO/RPO requirements.</p>
              {applicationProfilesLoading ? (
                <div className="flex items-center justify-center py-6 bg-slate-50 rounded-lg"><RefreshCw className="w-5 h-5 animate-spin text-slate-400 mr-2" /><span className="text-sm text-slate-500">Loading applications...</span></div>
              ) : applicationProfiles.length === 0 ? (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200"><p className="text-sm text-amber-800"><strong>No Application Profiles found.</strong> Complete Application Profile wizards in the IT-DRP section to see applications here.</p></div>
              ) : (
                <div className="grid md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                  {applicationProfiles.map((app) => {
                    const isSelected = (formData.application_dependencies || []).includes(app.id);
                    return (
                      <div key={app.id} onClick={() => toggleApplicationDependency(app.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${isSelected ? "border-cyan-500 bg-cyan-50 ring-1 ring-cyan-400" : "border-slate-200 hover:border-cyan-300 bg-white"}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{app.application_name}</p>
                            <p className="text-xs text-slate-500">{app.business_owner && `Owner: ${app.business_owner}`}{app.deployment_type && ` \u2022 ${app.deployment_type}`}</p>
                            {app.criticality && <Badge variant="outline" className="text-xs mt-1 capitalize">{app.criticality}</Badge>}
                          </div>
                          {isSelected && <CheckCircle2 className="w-5 h-5 text-cyan-600" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                <Label className="text-sm text-cyan-800 mb-2 block">Add Custom/Other Application Dependency</Label>
                <div className="flex gap-2">
                  <Input placeholder="e.g., Legacy ERP System, External CRM" value={customAppDependency} onChange={(e) => setCustomAppDependency(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomAppDependency())} className="flex-1" />
                  <Button type="button" variant="outline" size="sm" onClick={addCustomAppDependency}><Plus className="w-4 h-4 mr-1" />Add</Button>
                </div>
                {(formData.custom_application_dependencies || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">{(formData.custom_application_dependencies || []).map((dep, idx) => (<Badge key={idx} variant="secondary" className="gap-1 bg-cyan-100">{dep}<button onClick={() => removeCustomAppDependency(idx)} className="ml-1 hover:text-red-500">&times;</button></Badge>))}</div>
                )}
              </div>
              {(formData.application_dependencies || []).length > 0 && (<p className="text-xs text-cyan-600">{(formData.application_dependencies || []).length} application(s) selected - These will inherit RTO: {calculatedPreview?.rto_hours || '?'}h, RPO: {calculatedPreview?.rpo_hours || '?'}h</p>)}

              {/* V22.9.8 — Federated vendor connectivity links from the selected Application Profiles */}
              <ConnectivityLinksSurfaceImpl
                api={api}
                applicationIds={formData.application_dependencies || []}
                vendorDependencies={formData.vendor_dependencies || []}
                onQuickAddVendorDep={quickAddVendorFromConnectivity}
                onAddVendorDep={(name) => {
                  if (!name) return;
                  setNewVendor((prev) => ({ ...prev, vendor_name: name }));
                  setShowVendorForm(true);
                }}
              />
            </div>

            {/* Vendors */}
            <div className="space-y-3 mt-6 pt-6 border-t">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base font-semibold"><Truck className="w-4 h-4 text-purple-600" />Critical Vendors (RTO Inherited)</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowVendorForm(!showVendorForm)}><Plus className="w-4 h-4 mr-1" />Add Vendor</Button>
              </div>
              <p className="text-sm text-slate-600">Add third-party vendors this process depends on. They inherit your process's criticality tier, RTO, and RPO.</p>
              {showVendorForm && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                  <p className="text-sm text-purple-800 font-medium">Add Vendor with Contact Details (Required for RTO Inheritance)</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Vendor Name *</Label><AssetPicker api={api} type="vendor" value={newVendor.vendor_name ? { name: newVendor.vendor_name } : null} onChange={(v) => setNewVendor({ ...newVendor, vendor_name: v?.name || "", vendor_picker_id: v?.id || null, vendor_picker_source: v?.source || null })} testIdSuffix="bia" /></div>
                    <div><Label className="text-xs">Vendor Type</Label><Select value={newVendor.vendor_type} onValueChange={(v) => setNewVendor({ ...newVendor, vendor_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="technology">Technology/SaaS</SelectItem><SelectItem value="supplier">Supplier</SelectItem><SelectItem value="service_provider">Service Provider</SelectItem><SelectItem value="contractor">Contractor</SelectItem></SelectContent></Select></div>
                    <div><Label className="text-xs">Criticality</Label><Select value={newVendor.criticality} onValueChange={(v) => setNewVendor({ ...newVendor, criticality: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></div>
                    <div><Label className="text-xs">Contact Name</Label><Input placeholder="Account Manager" value={newVendor.contact_name} onChange={(e) => setNewVendor({ ...newVendor, contact_name: e.target.value })} /></div>
                    <div><Label className="text-xs">Contact Email *</Label><Input type="email" placeholder="support@vendor.com" value={newVendor.contact_email} onChange={(e) => setNewVendor({ ...newVendor, contact_email: e.target.value })} /></div>
                    <div><Label className="text-xs">Contact Phone</Label><Input placeholder="+1 555-0123" value={newVendor.contact_phone} onChange={(e) => setNewVendor({ ...newVendor, contact_phone: e.target.value })} /></div>
                    <div className="md:col-span-2"><Label className="text-xs">Address</Label><Input placeholder="123 Business St, City, State" value={newVendor.address} onChange={(e) => setNewVendor({ ...newVendor, address: e.target.value })} /></div>
                    <div className="md:col-span-2"><Label className="text-xs">Services Provided (comma-separated)</Label><Input placeholder="Cloud hosting, Database, API services" value={newVendor.services_provided} onChange={(e) => setNewVendor({ ...newVendor, services_provided: e.target.value })} /></div>
                  </div>
                  <div className="flex gap-2 justify-end"><Button type="button" variant="outline" size="sm" onClick={() => setShowVendorForm(false)}>Cancel</Button><Button type="button" size="sm" onClick={addEnhancedVendor}>Add Vendor</Button></div>
                </div>
              )}
              {formData.vendor_dependencies.length > 0 && (
                <div className="space-y-2">
                  {formData.vendor_dependencies.map((vendor) => (
                    <div key={vendor.id} className="p-3 bg-white border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2"><p className="font-medium">{vendor.vendor_name}</p><Badge variant="outline" className="text-xs">{vendor.vendor_type}</Badge><Badge className={`text-xs ${vendor.criticality === "critical" ? "bg-red-100 text-red-700" : vendor.criticality === "high" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-700"}`}>{vendor.criticality}</Badge>
                            {vendor.source_connectivity_link && (
                              <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 gap-0.5" data-testid={`vendor-dep-${vendor.id}-quick-added`}>
                                <Cable className="w-2.5 h-2.5" />Quick-added
                              </Badge>
                            )}
                            {vendor.needs_contact_email && !vendor.contact_email && (
                              <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-300" data-testid={`vendor-dep-${vendor.id}-needs-email`}>
                                Needs contact email
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{vendor.contact_email} {vendor.contact_phone && `\u2022 ${vendor.contact_phone}`}</p>
                          <div className="mt-2 p-2 bg-emerald-50 rounded text-xs space-y-1"><p className="text-emerald-700 font-medium">Inherited from Process:</p><div className="flex flex-wrap gap-3 text-emerald-600"><span>Criticality: <strong>{calculatedPreview?.criticality_tier || formData.criticality_tier || 'Tier based on RTO'}</strong></span><span>RTO: <strong>{calculatedPreview?.rto_hours || formData.calculated_rto_hours || '?'}h</strong></span><span>RPO: <strong>{calculatedPreview?.rpo_hours || formData.calculated_rpo_hours || '?'}h</strong></span></div></div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeEnhancedVendor(vendor.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {formData.critical_vendors.length > 0 && (<div className="flex flex-wrap gap-2 mt-2">{formData.critical_vendors.map((vendor, idx) => (<Badge key={idx} variant="secondary" className="gap-1">{vendor}<button onClick={() => removeVendor(idx)} className="ml-1 hover:text-red-500">&times;</button></Badge>))}</div>)}
            </div>

            {/* Facilities */}
            <div className="space-y-3 mt-6 pt-6 border-t">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2 text-base font-semibold"><Building2 className="w-4 h-4 text-emerald-600" />Critical Facilities (RTO + Activation Time)</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowFacilityForm(!showFacilityForm)}><Plus className="w-4 h-4 mr-1" />Add Facility</Button>
              </div>
              <p className="text-sm text-slate-600">Add facilities this process depends on. Facility RTO = Process RTO + Activation Time.</p>
              {showFacilityForm && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-3">
                  <p className="text-sm text-emerald-800 font-medium">Add Facility with Recovery Details</p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Facility Name *</Label><Input placeholder="e.g., Primary Data Center" value={newFacility.facility_name} onChange={(e) => setNewFacility({ ...newFacility, facility_name: e.target.value })} /></div>
                    <div><Label className="text-xs">Facility Type</Label><Select value={newFacility.facility_type} onValueChange={(v) => setNewFacility({ ...newFacility, facility_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="primary">Primary</SelectItem><SelectItem value="secondary">Secondary/DR</SelectItem><SelectItem value="alternate">Alternate</SelectItem><SelectItem value="remote">Remote/WFH</SelectItem></SelectContent></Select></div>
                    <div><Label className="text-xs">City</Label><Input placeholder="New York" value={newFacility.city} onChange={(e) => setNewFacility({ ...newFacility, city: e.target.value })} /></div>
                    <div><Label className="text-xs">Activation Time (hours)</Label><Input type="number" min="0" step="0.5" placeholder="2" value={newFacility.activation_time_hours} onChange={(e) => setNewFacility({ ...newFacility, activation_time_hours: parseFloat(e.target.value) || 0 })} /></div>
                    <div><Label className="text-xs">Capacity (%)</Label><Input type="number" min="0" max="100" value={newFacility.capacity_percentage} onChange={(e) => setNewFacility({ ...newFacility, capacity_percentage: parseInt(e.target.value) || 100 })} /></div>
                    <div className="flex items-center gap-2 pt-5"><Switch checked={newFacility.is_remote_capable} onCheckedChange={(checked) => setNewFacility({ ...newFacility, is_remote_capable: checked })} /><Label className="text-xs">Remote Capable</Label></div>
                    <div className="md:col-span-2"><Label className="text-xs">Address</Label><Input placeholder="Full address" value={newFacility.address} onChange={(e) => setNewFacility({ ...newFacility, address: e.target.value })} /></div>
                  </div>
                  <div className="flex gap-2 justify-end"><Button type="button" variant="outline" size="sm" onClick={() => setShowFacilityForm(false)}>Cancel</Button><Button type="button" size="sm" onClick={addEnhancedFacility}>Add Facility</Button></div>
                </div>
              )}
              {formData.facility_dependencies.length > 0 && (
                <div className="space-y-2">
                  {formData.facility_dependencies.map((facility) => (
                    <div key={facility.id} className="p-3 bg-white border rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2"><p className="font-medium">{facility.facility_name}</p><Badge variant="outline" className="text-xs capitalize">{facility.facility_type}</Badge>{facility.is_remote_capable && <Badge className="text-xs bg-blue-100 text-blue-700">Remote OK</Badge>}</div>
                          <p className="text-xs text-slate-500 mt-1">{facility.city} &bull; {facility.capacity_percentage}% capacity</p>
                          <div className="mt-2 p-2 bg-emerald-50 rounded text-xs space-y-1"><p className="text-emerald-700 font-medium">Inherited from Process:</p><div className="flex flex-wrap gap-3 text-emerald-600"><span>Criticality: <strong>{calculatedPreview?.criticality_tier || formData.criticality_tier || 'Tier based on RTO'}</strong></span><span>RTO: <strong>{(calculatedPreview?.rto_hours || formData.calculated_rto_hours || 0) + (facility.activation_time_hours || 0)}h</strong> (includes +{facility.activation_time_hours || 0}h activation)</span><span>RPO: <strong>{calculatedPreview?.rpo_hours || formData.calculated_rpo_hours || '?'}h</strong></span></div></div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeEnhancedFacility(facility.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {formData.critical_facilities.length > 0 && (<div className="flex flex-wrap gap-2 mt-2">{formData.critical_facilities.map((facility, idx) => (<Badge key={idx} variant="secondary" className="gap-1">{facility}<button onClick={() => removeFacility(idx)} className="ml-1 hover:text-red-500">&times;</button></Badge>))}</div>)}
            </div>
          </div>
        )}

        {/* Step 4: Resources */}
        {currentStep === 4 && (
          <div className="space-y-6" data-testid="step-resources">
            <div className="space-y-4">
              <Label className="flex items-center gap-2 text-base font-semibold"><Users className="w-4 h-4 text-slate-400" />Resource Requirements</Label>

              {/* D1 — per-role staffing editor */}
              <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="staffing-roles-editor">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Per-Role Staffing (optional)</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addStaffingRole} data-testid="add-staffing-role">+ Add Role</Button>
                </div>
                {staffingRoles.length === 0 && (
                  <p className="text-xs text-slate-500">Add roles to derive the aggregates below automatically, or fill the totals directly.</p>
                )}
                {staffingRoles.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`staffing-role-${i}`}>
                    <Input className="col-span-4" placeholder="Role name" value={r.role} onChange={(e) => updateStaffingRole(i, "role", e.target.value)} />
                    <Input className="col-span-2" type="number" min="0" placeholder="Total" value={r.total} onChange={(e) => updateStaffingRole(i, "total", parseInt(e.target.value) || 0)} />
                    <Input className="col-span-2" type="number" min="0" placeholder="Min" value={r.minimum} onChange={(e) => updateStaffingRole(i, "minimum", parseInt(e.target.value) || 0)} />
                    <Input className="col-span-2" type="number" min="0" step="0.5" placeholder="Time→min (h)" value={r.time_to_min_hours} onChange={(e) => updateStaffingRole(i, "time_to_min_hours", parseFloat(e.target.value) || 0)} />
                    <label className="col-span-1 flex items-center gap-1 text-xs"><input type="checkbox" checked={!!r.remote_capable} onChange={(e) => updateStaffingRole(i, "remote_capable", e.target.checked)} />Rem</label>
                    <Button type="button" size="sm" variant="ghost" className="col-span-1 text-red-600" onClick={() => removeStaffingRole(i)}>✕</Button>
                  </div>
                ))}
                {rolesActive && (
                  <p className="text-xs font-medium text-emerald-700" data-testid="derived-staffing-label">Derived from per-role staffing: Total {derivedStaffing.total} · Min {derivedStaffing.minimum} · Time-to-min {derivedStaffing.ttm}h</p>
                )}
              </div>

              {/* Input A: Total Resources */}
              <div className="space-y-2">
                <Label className="text-sm">Input A: Total Existing Resources {rolesActive && <span className="text-xs text-emerald-700">(Derived from per-role staffing)</span>}</Label>
                <Input data-testid="total-staff" type="number" min="0" disabled={rolesActive} value={rolesActive ? derivedStaffing.total : formData.total_staff} onChange={(e) => setFormData({ ...formData, total_staff: parseInt(e.target.value) || 0 })} />
              </div>
              
              {/* Input B: Progressive - only after Input A */}
              {derivedStaffing.total > 0 && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                  <Label className="text-sm">Input B: Minimum Resources for Recovery {rolesActive && <span className="text-xs text-emerald-700">(Derived)</span>}</Label>
                  <Input data-testid="minimum-staff" type="number" min="0" max={derivedStaffing.total} disabled={rolesActive} value={rolesActive ? derivedStaffing.minimum : formData.minimum_staff} onChange={(e) => setFormData({ ...formData, minimum_staff: parseInt(e.target.value) || 0 })} />
                </div>
              )}
              
              {/* Input C: Progressive - only after Input B */}
              {derivedStaffing.total > 0 && derivedStaffing.minimum > 0 && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                  <Label className="text-sm">Input C: Recovery Timeframe for Minimum (Hours) {rolesActive && <span className="text-xs text-emerald-700">(Derived)</span>}</Label>
                  <Input data-testid="time-to-min" type="number" min="0" step="0.5" disabled={rolesActive} value={rolesActive ? derivedStaffing.ttm : formData.time_to_min_staff_hours} onChange={(e) => setFormData({ ...formData, time_to_min_staff_hours: parseFloat(e.target.value) || 0 })} />
                </div>
              )}
              
              {/* Remaining Resources Calculation — Progressive */}
              {formData.total_staff > 0 && formData.minimum_staff > 0 && formData.total_staff > formData.minimum_staff && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    <Label className="text-sm font-semibold text-amber-800">Remaining Resources: {formData.total_staff - formData.minimum_staff}</Label>
                  </div>
                  <p className="text-xs text-amber-700">System Calculation: {formData.total_staff} (Total) - {formData.minimum_staff} (Minimum) = <strong>{formData.total_staff - formData.minimum_staff}</strong> resources still need recovery after initial minimum staffing.</p>
                  <div className="space-y-2">
                    <Label className="text-sm text-amber-800">How soon do you require the remaining {formData.total_staff - formData.minimum_staff} resources?</Label>
                    <Input data-testid="remaining-recovery-hours" type="number" min="0" step="0.5" className="max-w-xs bg-white" value={formData.remaining_resource_recovery_hours} onChange={(e) => setFormData({ ...formData, remaining_resource_recovery_hours: parseFloat(e.target.value) || 0 })} placeholder="e.g., 24 hours" />
                    {formData.remaining_resource_recovery_hours > 0 && (
                      <p className="text-xs text-amber-600 mt-1">A Resource BCP task will be auto-generated for the resource owner when this BIA is approved.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Remote Capability Toggle — V3 */}
            <div className="space-y-4 pt-4 border-t">
              <Label className="flex items-center gap-2 text-base font-semibold"><Home className="w-4 h-4 text-blue-500" />Remote Work Capability</Label>
              <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                formData.resource_is_remote_capable ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-slate-50'
              }`}>
                <div className="flex items-center gap-3">
                  {formData.resource_is_remote_capable ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <Building2 className="w-5 h-5 text-slate-400" />
                  )}
                  <div>
                    <p className="font-medium text-sm">{formData.resource_is_remote_capable ? "Yes — Resources can work from home" : "No — Resources require a physical facility"}</p>
                    <p className="text-xs text-slate-500">{formData.resource_is_remote_capable ? "No facility dependency required for these resources." : "You must link a Facility Asset below."}</p>
                  </div>
                </div>
                <Switch checked={formData.resource_is_remote_capable} onCheckedChange={(checked) => setFormData({ ...formData, resource_is_remote_capable: checked, ...(!checked ? {} : { primary_facility_id: "" }) })} data-testid="remote-capable-toggle" />
              </div>
              
              {/* Facility Linking — Only if NOT remote-capable */}
              {!formData.resource_is_remote_capable && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    <Label className="text-sm font-semibold text-blue-800">Link Primary Facility</Label>
                    <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">Required</Badge>
                  </div>
                  <p className="text-xs text-blue-700">Since resources cannot work remotely, link the physical facility where they operate. The facility will inherit this process's criticality tier and RTO/RPO.</p>
                  <Select value={formData.primary_facility_id} onValueChange={(val) => setFormData({ ...formData, primary_facility_id: val })}>
                    <SelectTrigger data-testid="primary-facility-select" className="bg-white">
                      <SelectValue placeholder="Select facility asset..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(formData.facility_dependencies || []).map(fd => (
                        <SelectItem key={fd.facility_id || fd.id} value={fd.facility_id || fd.id}>{fd.name || fd.facility_name || fd.facility_id}</SelectItem>
                      ))}
                      {(formData.critical_facilities || []).map(cf => (
                        <SelectItem key={cf} value={cf}>{cf}</SelectItem>
                      ))}
                      <SelectItem value="other_facility">Other (specify in dependencies)</SelectItem>
                    </SelectContent>
                  </Select>
                  {!formData.primary_facility_id && (
                    <p className="text-xs text-red-500">A primary facility must be linked for non-remote resources.</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Department Head Approver */}
            <div className="space-y-4 pt-4 border-t">
              <Label className="flex items-center gap-2 text-base font-semibold"><Shield className="w-4 h-4 text-indigo-500" />Department Head Approver <Badge variant="outline" className="text-xs">Optional</Badge></Label>
              <p className="text-sm text-slate-500">Appoint a Department Head as an additional approver for recovery plans generated from this BIA. This person will be notified when the BIA is approved.</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2"><Label className="text-sm">Name</Label><Input data-testid="dept-head-name" placeholder="e.g., Sarah Johnson" value={formData.department_head_name} onChange={(e) => setFormData({ ...formData, department_head_name: e.target.value })} /></div>
                <div className="space-y-2"><Label className="text-sm">Email</Label><Input data-testid="dept-head-email" type="email" placeholder="e.g., sarah@company.com" value={formData.department_head_email} onChange={(e) => setFormData({ ...formData, department_head_email: e.target.value })} /></div>
                <div className="space-y-2"><Label className="text-sm">ID / Employee Code</Label><Input data-testid="dept-head-id" placeholder="e.g., EMP-001" value={formData.department_head_id} onChange={(e) => setFormData({ ...formData, department_head_id: e.target.value })} /></div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <Label className="flex items-center gap-2 text-base font-semibold"><Database className="w-4 h-4 text-slate-400" />Data Loss Tolerance (RPO)</Label>

              {/* D2 — per-asset data sensitivity */}
              {selectedAssetsForSensitivity.length > 0 && (
                <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="asset-sensitivity-editor">
                  <Label className="text-sm font-semibold">Per-Asset Data Sensitivity</Label>
                  <p className="text-xs text-slate-500">For each application / equipment this process depends on, does it hold sensitive data? Sensitive assets set the process RPO to the strictest value.</p>
                  {selectedAssetsForSensitivity.map((asset) => {
                    const s = getSensitivity(asset.asset_ref) || {};
                    const yes = !!s.holds_sensitive_data;
                    const arpo = yes && s.data_loss_tolerance_hours != null ? rpoFromDataLoss(s.data_loss_tolerance_hours, s.data_loss_financial_impact) : null;
                    return (
                      <div key={asset.asset_ref} className="border-b border-slate-200 py-2" data-testid={`asset-sensitivity-${asset.asset_ref}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{asset.asset_name} <span className="text-xs text-slate-400">({asset.asset_type})</span></span>
                          <div className="flex items-center gap-2 text-xs">
                            <span>Holds sensitive data?</span>
                            <Switch checked={yes} onCheckedChange={(c) => setSensitivity(asset, { holds_sensitive_data: c })} />
                            <span>{yes ? "Yes" : "No"}</span>
                          </div>
                        </div>
                        {yes && (
                          <div className="grid md:grid-cols-2 gap-2 mt-2">
                            <Input type="number" min="0" step="0.5" placeholder="Tolerance (hours)" value={s.data_loss_tolerance_hours ?? ""} onChange={(e) => setSensitivity(asset, { data_loss_tolerance_hours: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                            <Input type="number" min="0" placeholder="Financial impact ($)" value={s.data_loss_financial_impact ?? ""} onChange={(e) => setSensitivity(asset, { data_loss_financial_impact: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                            {arpo != null && (
                              <p className="col-span-2 text-xs text-indigo-700">Per-asset RPO: <strong>{arpo}h</strong> → {protectionHint(arpo)}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {strictestAssetRpo && (
                    <p className="text-xs font-medium text-emerald-700" data-testid="strictest-asset-rpo">Process RPO set by strictest asset: {strictestAssetRpo.name} ({strictestAssetRpo.rpo}h)</p>
                  )}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-sm">Max Acceptable Data Loss (hours){strictestAssetRpo && <span className="text-xs text-slate-400"> — fallback (per-asset overrides)</span>}</Label><Input type="number" min="0" step="0.5" value={formData.data_loss_tolerance_hours} onChange={(e) => setFormData({ ...formData, data_loss_tolerance_hours: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-2"><Label className="text-sm">Financial Impact of Data Loss ($)</Label><Input type="number" min="0" value={formData.data_loss_financial_impact} onChange={(e) => setFormData({ ...formData, data_loss_financial_impact: parseFloat(e.target.value) || 0 })} /></div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <Switch checked={formData.remote_operation_possible} onCheckedChange={(checked) => setFormData({ ...formData, remote_operation_possible: checked })} />
              <div><Label>Remote Operation Possible (Process-Level)</Label><p className="text-sm text-slate-500">Can this entire process operate remotely if the primary facility is unavailable?</p></div>
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {currentStep === 5 && (
          <div className="space-y-6" data-testid="step-review">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base">Process Summary</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Name:</span><span className="font-medium">{formData.process_name || "Not set"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Department:</span><span>{formData.department || "Not set"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Owner:</span><span>{formData.process_owner_name || "Not set"}</span></div>
                  <div className="pt-2 border-t mt-2">
                    <span className="text-slate-500 text-xs">Asset Types:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.asset_types.length > 0 ? formData.asset_types.map(typeId => { const a = ASSET_TYPE_OPTIONS.find(x => x.id === typeId); return a ? <Badge key={typeId} variant="outline" className="text-xs bg-sky-50">{a.icon} {a.name}</Badge> : null; }) : <span className="text-xs text-slate-400">None selected</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className={calculatedPreview ? getRTOBandColor(calculatedPreview.rto_band) : ""}>
                <CardHeader className="pb-3"><CardTitle className="text-base">Calculated Recovery Metrics</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">RTO (Recovery Time Objective):</span><Badge className="text-lg">{calculatedPreview?.rto_hours || 168}h</Badge></div>
                  <div className="flex items-center justify-between pt-2 border-t"><span className="text-sm font-medium">RPO (Recovery Point Objective):</span><Badge variant="outline" className="text-lg">{strictestAssetRpo ? strictestAssetRpo.rpo : Math.min(formData.data_loss_tolerance_hours, formData.data_loss_financial_impact > 500000 ? 1 : formData.data_loss_financial_impact > 100000 ? 4 : formData.data_loss_financial_impact > 50000 ? 8 : 24)}h</Badge></div>
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">Staff Recovery Time:</span><Badge variant="secondary">{derivedStaffing.total > 0 && derivedStaffing.minimum > 0 ? `${(derivedStaffing.ttm * (derivedStaffing.minimum / derivedStaffing.total >= 0.75 ? 1.25 : derivedStaffing.minimum / derivedStaffing.total >= 0.5 ? 1.5 : 2.0)).toFixed(1)}h` : "N/A"}</Badge></div>
                  {derivedStaffing.total > derivedStaffing.minimum && derivedStaffing.minimum > 0 && (
                    <>
                      <div className="flex items-center justify-between pt-2 border-t"><span className="text-sm font-medium">Remaining Resources:</span><Badge variant="outline" className="bg-amber-50 text-amber-800">{derivedStaffing.total - derivedStaffing.minimum}</Badge></div>
                      {formData.remaining_resource_recovery_hours > 0 && (
                        <div className="flex items-center justify-between"><span className="text-sm font-medium">Full Recovery Timeframe:</span><Badge variant="outline" className="bg-amber-50 text-amber-800">{formData.remaining_resource_recovery_hours}h</Badge></div>
                      )}
                    </>
                  )}
                  {formData.department_head_name && (
                    <div className="flex items-center justify-between pt-2 border-t"><span className="text-sm font-medium">Dept Head Approver:</span><Badge variant="outline" className="bg-indigo-50 text-indigo-800">{formData.department_head_name}</Badge></div>
                  )}
                  {/* V3: Remote Capability & Facility Link */}
                  <div className="flex items-center justify-between pt-2 border-t"><span className="text-sm font-medium">Remote Capable:</span><Badge variant="outline" className={formData.resource_is_remote_capable ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}>{formData.resource_is_remote_capable ? "Yes" : "No"}</Badge></div>
                  {!formData.resource_is_remote_capable && formData.primary_facility_id && (
                    <div className="flex items-center justify-between"><span className="text-sm font-medium">Primary Facility:</span><Badge variant="outline" className="bg-blue-50 text-blue-700">{formData.primary_facility_id}</Badge></div>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="border-sky-200 bg-sky-50">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Zap className="w-5 h-5 text-sky-700" />RTO Derivation Explanation</CardTitle></CardHeader>
              <CardContent className="text-sm text-sky-900">
                <p className="mb-2">{calculatedPreview?.explanation || "RTO will be calculated from your 5x4 Impact Matrix."}</p>
                <div className="p-3 bg-white/60 rounded-lg mt-2"><p className="text-xs text-sky-700"><strong>How it works:</strong> RTO is set to the <em>first time band</em> where any impact category reaches &quot;High&quot;. If the Sensitivity Anchor (set by BCM Manager) also reaches High at the same time, it takes priority.</p></div>
              </CardContent>
            </Card>
            <Card className="border-emerald-200 bg-emerald-50">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Database className="w-5 h-5 text-emerald-600" />RPO Derivation Explanation</CardTitle></CardHeader>
              <CardContent className="text-sm text-emerald-900">
                <p className="mb-2">{getRPOExplanation()}</p>
                <div className="p-3 bg-white/60 rounded-lg mt-2"><p className="text-xs text-emerald-700"><strong>RPO Tiers:</strong> $500K+ impact &rarr; Max 1h | $100K+ &rarr; Max 4h | $50K+ &rarr; Max 8h | Below &rarr; Max 24h</p></div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Users className="w-5 h-5 text-amber-600" />Staff Recovery Time Explanation</CardTitle></CardHeader>
              <CardContent className="text-sm text-amber-900"><p>{getStaffRTExplanation()}</p></CardContent>
            </Card>

            {getRTOConflicts().length > 0 && (
              <Card className="border-red-300 bg-red-50" data-testid="review-rto-conflicts">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2 text-red-800"><AlertTriangle className="w-5 h-5 text-red-600" />RTO Conflicts Will Be Created ({getRTOConflicts().length})</CardTitle></CardHeader>
                <CardContent className="text-sm text-red-900">
                  <p className="mb-3">Upon submission, the following upstream process owners will be notified to reduce their RTO:</p>
                  <div className="space-y-2">
                    {getRTOConflicts().map((conflict) => (
                      <div key={conflict.processId} className="p-3 bg-white rounded-lg border border-red-200">
                        <div className="flex items-center justify-between mb-1"><span className="font-medium">{conflict.processName}</span><Badge variant={conflict.severity === "high" ? "destructive" : "warning"}>{conflict.severity.toUpperCase()} GAP</Badge></div>
                        <p className="text-xs text-red-700">Current RTO: {conflict.upstreamRTO}h &rarr; Required: &le;{conflict.currentRTO}h (Gap: {conflict.gap}h)</p>
                        <p className="text-xs text-slate-600 mt-1 italic">Notification will be sent: &quot;Downstream process &apos;{formData.process_name || "This Process"}&apos; requires your RTO to be {conflict.currentRTO}h or less&quot;</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-2 bg-amber-100 rounded text-xs text-amber-800"><strong>Note:</strong> You can still submit. The upstream process owner will review these gaps and can auto-align RTOs if approved.</div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Dependencies Summary</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Upstream ({formData.upstream_process_ids.length + formData.custom_upstream_dependencies.length})</p>
                    {formData.upstream_process_ids.length === 0 && formData.custom_upstream_dependencies.length === 0 ? <p className="text-sm text-slate-400">None selected</p> : (
                      <div className="space-y-1">
                        {formData.upstream_process_ids.map(id => { const proc = availableProcesses.find(p => p.id === id); return proc ? <Badge key={id} variant="outline" className="mr-1">{proc.process_name}</Badge> : null; })}
                        {formData.custom_upstream_dependencies.map((dep, idx) => <Badge key={`custom-up-${idx}`} variant="secondary" className="mr-1 bg-blue-100">{dep} (External)</Badge>)}
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Downstream ({formData.downstream_process_ids.length + formData.custom_downstream_dependencies.length})</p>
                    {formData.downstream_process_ids.length === 0 && formData.custom_downstream_dependencies.length === 0 ? <p className="text-sm text-slate-400">None selected</p> : (
                      <div className="space-y-1">
                        {formData.downstream_process_ids.map(id => { const proc = availableProcesses.find(p => p.id === id); return proc ? <Badge key={id} variant="outline" className="mr-1">{proc.process_name}</Badge> : null; })}
                        {formData.custom_downstream_dependencies.map((dep, idx) => <Badge key={`custom-down-${idx}`} variant="secondary" className="mr-1 bg-purple-100">{dep} (External)</Badge>)}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Resources & Vendors</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4 text-sm">
                  <div><p className="text-slate-500">Staffing</p><p className="font-medium" data-testid="review-staffing-summary">{derivedStaffing.minimum}/{derivedStaffing.total} min/total</p></div>
                  <div><p className="text-slate-500">Vendors (with RTO)</p><p className="font-medium">{formData.vendor_dependencies.length} configured</p></div>
                  <div><p className="text-slate-500">Facilities</p><p className="font-medium">{formData.facility_dependencies.length} configured</p></div>
                  <div><p className="text-slate-500">Remote Capable</p><p className="font-medium">{formData.remote_operation_possible ? "Yes" : "No"}</p></div>
                </div>
                {formData.vendor_dependencies.length > 0 && (
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs font-semibold text-purple-800 mb-2">Vendor RTO Inheritance Preview:</p>
                    <div className="space-y-1">{formData.vendor_dependencies.map(v => (<div key={v.id} className="flex justify-between text-xs"><span>{v.vendor_name}</span><span className="text-purple-600">&rarr; Will inherit {calculatedPreview?.rto_hours || 168}h RTO</span></div>))}</div>
                  </div>
                )}
                {formData.facility_dependencies.length > 0 && (
                  <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
                    <p className="text-xs font-semibold text-emerald-800 mb-2">Facility Effective RTO Preview:</p>
                    <div className="space-y-1">{formData.facility_dependencies.map(f => (<div key={f.id} className="flex justify-between text-xs"><span>{f.facility_name} ({f.facility_type})</span><span className="text-emerald-600">&rarr; {(calculatedPreview?.rto_hours || 168) + f.activation_time_hours}h ({calculatedPreview?.rto_hours || 168}h + {f.activation_time_hours}h activation)</span></div>))}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <div>{currentStep > 1 && (<Button variant="outline" onClick={() => setCurrentStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Previous</Button>)}</div>
          <div className="flex gap-2">
            {currentStep < 5 && (<Button variant="outline" onClick={() => setCurrentStep(s => s + 1)}>Next <ChevronRight className="w-4 h-4 ml-1" /></Button>)}
            {currentStep === 5 && (<Button onClick={handleSubmit} disabled={saving} className={mode === "technical" ? "bg-violet-700 hover:bg-violet-800" : "bg-sky-700 hover:bg-sky-800"} data-testid="wizard-submit-btn">{saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}{editProcess ? (mode === "technical" ? "Update Technical BIA" : "Update Process BIA") : (mode === "technical" ? "Create Technical BIA" : "Create Process BIA")}</Button>)}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
